// ArtistQueueView.js
// Dan's iPad interface. Shows all active orders in a horizontal filmstrip.
// Tapping a card opens the full-screen reference image view.
// State transitions: queued → drawing → cooking → flipping → ready_to_deliver → done
//
// Shift-aware: listens for an active shift document. If no shift is open,
// shows the ShiftManager "Open Queue" screen instead of the filmstrip.
// When Dan taps Start on an order, broadcasts currentNumber to the shift
// document — this is what customer confirmed screens use for wait time display.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { db, storage } from "./firebase";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { extractDominantColors, generateColorIsolation } from "./utils/colorExtract";
import {
  addDoc, collection, query, where, onSnapshot,
  doc, updateDoc, serverTimestamp, runTransaction, setDoc,
  getDocs, orderBy, limit, increment, writeBatch
} from "firebase/firestore";
import GridOverlay from "./GridOverlay";
import ShiftManager from "./ShiftManager";
import FramingTool from "./FramingTool";
import { COLOR_OPTIONS } from "./flashDesigns";
import { normalizePhotoForUpload } from "./utils/photo";
import useFlashDesigns from "./hooks/useFlashDesigns";
import useArtists from "./hooks/useArtists";
import useActiveShift from "./hooks/useActiveShift";
import ShiftPicker, { ShiftChip } from "./components/ShiftPicker";
import { writeAlert } from "./utils/alerts";
import ReissueTicketModal from "./components/ReissueTicketModal";
import { STAFF_THEME, FONT } from "./theme/staff";
import { baselineWaitMinutes, shiftTempo, TEMPO_SAMPLE_WINDOW } from "./utils/overlayFormat";
import { COMBOS, COMBO_ORDER, ADD_ONS, addOnLabel, addOnDeliveryPrompt, fulfillAllAddOns, unfulfilledAddOns, comboAddOnsFor, shiftSellsGoods } from "./utils/combos";

// BRAND sourced from shared staff theme. `blue` here aliases sky for the
// drawing-state badge (not to be confused with the richer brand blue).
const BRAND = { ...STAFF_THEME, blue: STAFF_THEME.sky };

const STATE_LABELS = {
  queued:            "QUEUED",
  drawing:           "DRAWING",
  cooking:           "COOKING",
  flipping:          "FLIPPING",
  suspended:         "SUSPENDED",
  ready_to_deliver:  "READY TO HAND OFF",
  done:              "DONE",
  reference:         "⚡ REFERENCE — NO TICKET",
};

const STATE_COLORS = {
  queued:            BRAND.gold,
  drawing:           BRAND.blue,
  cooking:           "#f5a623",
  flipping:          BRAND.orange,
  suspended:         "rgba(255,255,255,0.55)",
  ready_to_deliver:  "#00e5cc",
  done:              BRAND.pink,
  reference:         BRAND.sky,
};

// Synthetic VIEW-ONLY order for the flash reference picker. state "reference"
// matches none of the work view's isQueued/isDrawing/etc flags, so every
// write-capable action bar self-hides and only "← Back to Queue" renders.
// Never write this to Firestore — it has no doc.
function makeFlashRefOrder(design) {
  return {
    id:            `flashref:${design.id}`,
    _isReference:  true,
    state:         "reference",
    orderType:     "flash",
    designId:      design.id,
    designLabel:   design.label,
    recipientName: design.label,
  };
}

const DEFAULT_GRID = {
  show:            true,
  color:           "#00ffff",
  opacity:         0.45,
  showCircle:      true,
  showLabels:      true,
  side:            null,    // "left" | "right" | null
  fullGriddle:     false,
};

// Format milliseconds as mm:ss. Returns "--:--" for null/undefined.
function fmtMs(ms) {
  if (ms == null || ms < 0) return "--:--";
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// Live stopwatch format: mm:ss.x (tenths of a second for that "pro timer" feel).
function fmtStopwatch(ms) {
  if (ms == null || ms < 0) return "--:--.0";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${tenths}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${tenths}`;
}

// Estimated remaining queue time in minutes.
// Type baselines × live shift tempo (see utils/overlayFormat.js — same model
// as the overlays). Drawing orders subtract elapsed time against their weight.
function computeQueueWait(orders, now, shift) {
  const FLIP_MIN    = 2;
  const DELIVER_MIN = 1;
  const tempo = shiftTempo(shift);
  let total = 0;
  for (const o of orders) {
    if (o.queue === "standby") continue; // standby orders don't count toward wait
    const base = baselineWaitMinutes(o) * tempo;
    if (o.state === "queued" || o.state === "suspended") {
      total += base;
    } else if (o.state === "drawing") {
      const elapsedMin = o.startedAt ? (now - o.startedAt.toMillis()) / 60000 : 0;
      total += Math.max(1, base - elapsedMin);
    } else if (o.state === "cooking" || o.state === "flipping") {
      total += FLIP_MIN;
    } else if (o.state === "ready_to_deliver") {
      total += DELIVER_MIN;
    }
  }
  return Math.round(total);
}

function fmtWaitTime(min) {
  if (min <= 0) return null;
  if (min < 60) return `~${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
}


// ── FLIP COUNTDOWN OVERLAY — shown on artist iPad when Flip! is tapped ──────
// Time-based: derives phase from elapsed time so it stays in sync with the
// overlay's FlipAnimation (both use the same phase boundaries).
// Phase timeline: primer 0-2s → 3 → 2 → 1 → FLIP! → done at 6.1s.
const FLIP_PHASES = [
  { end: 2000, id: "primer" },
  { end: 2900, id: 3 },
  { end: 3800, id: 2 },
  { end: 4700, id: 1 },
  { end: 6100, id: "flip" },
];
function getFlipPhase(elapsed) {
  for (const p of FLIP_PHASES) {
    if (elapsed < p.end) return p.id;
  }
  return "done";
}

function FlipCountdownOverlay({ startedAt, onDone }) {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const t0 = useRef(startedAt || Date.now());
  const [current, setCurrent] = useState(() => getFlipPhase(Date.now() - t0.current));

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - t0.current;
      const next = getFlipPhase(elapsed);
      if (next === "done") {
        clearInterval(id);
        onDoneRef.current();
      } else {
        setCurrent((prev) => (prev !== next ? next : prev));
      }
    }, 50);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const countColors = { 3: "#4aa4e1", 2: "#ef537e", 1: "#fcb31f" };

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 100,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.75)", pointerEvents: "none",
    }}>
      {current === "primer" && (
        <div style={{
          color: BRAND.gold, fontSize: "2.5rem", fontWeight: 900,
          textAlign: "center", letterSpacing: "0.08em",
          animation: "jopArtistPrimerPulse 0.8s ease-in-out",
        }}>
          HERE WE GO!
        </div>
      )}
      {typeof current === "number" && current > 0 && (
        <div key={current} style={{
          fontSize: "8rem", fontWeight: 900, lineHeight: 1,
          color: countColors[current] || "#fcb31f",
          textShadow: `0 0 30px ${countColors[current]}88`,
          animation: "jopArtistCountPop 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards",
        }}>{current}</div>
      )}
      {current === "flip" && (
        <div key="flip" style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          animation: "jopArtistFlipBurst 1s cubic-bezier(0.34,1.2,0.64,1) forwards",
        }}>
          <div style={{ fontSize: "5rem" }}>🥞</div>
          <div style={{
            fontWeight: 900, fontSize: "2.5rem", marginTop: "0.3rem",
            display: "flex", gap: "0.2em",
          }}>
            {"FLIP!".split("").map((ch, i) => {
              const colors = ["#4aa4e1", "#ef537e", "#fcb31f", "#b9d550", "#1d52a0"];
              return (
                <span key={i} style={{
                  color: colors[i],
                  textShadow: `0 0 20px ${colors[i]}88`,
                  display: "inline-block",
                  transform: `rotate(${[-4, 3, -2, 5, -3][i]}deg)`,
                }}>{ch}</span>
              );
            })}
          </div>
        </div>
      )}
      <style>{`
        @keyframes jopArtistPrimerPulse {
          0%   { opacity: 0; transform: scale(0.8); }
          50%  { opacity: 1; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes jopArtistCountPop {
          0%   { opacity: 0; transform: scale(0.3); }
          60%  { opacity: 1; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes jopArtistFlipBurst {
          0%   { opacity: 0; transform: scale(0.5); }
          50%  { opacity: 1; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// Atomic queue number assignment — same logic as CustomerOrderView/FlashOrderView
async function assignQueueNumber(shiftId) {
  const counterSnap = await getDocs(collection(db, "shifts", shiftId, "meta"));
  if (counterSnap.empty) return null;
  const counterRef = counterSnap.docs[0].ref;
  return runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    if (!counterDoc.exists()) throw new Error("Counter document missing");
    const current = counterDoc.data().lastNumber ?? 0;
    const next    = current + 1;
    transaction.update(counterRef, { lastNumber: next });
    return next;
  });
}

// ── QUEUE TABS — switch between Live (booth) and Studio ─────────────────────
function QueueTabs({ active, onSwitch, studioCount }) {
  return (
    <div style={{ display: "flex", gap: "0.4rem", padding: "0.5rem 1rem", background: "#0a1230", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      {[
        { id: "live", label: "Live", emoji: "🥞" },
        { id: "studio", label: "Studio", emoji: "🎨", badge: studioCount || null },
      ].map(t => (
        <button
          key={t.id}
          onClick={() => onSwitch(t.id)}
          style={{
            background: active === t.id ? "#fcb31f" : "transparent",
            color: active === t.id ? "#0d1833" : "rgba(255,218,173,0.55)",
            border: active === t.id ? "1px solid #fcb31f" : "1px solid rgba(255,255,255,0.12)",
            borderRadius: "8px", padding: "0.4rem 0.8rem",
            fontSize: "0.85rem", fontWeight: active === t.id ? 800 : 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: "0.3rem",
          }}
        >
          <span>{t.emoji}</span>
          <span>{t.label}</span>
          {t.badge > 0 && (
            <span style={{
              background: active === t.id ? "rgba(0,0,0,0.2)" : "rgba(252,179,31,0.2)",
              color: active === t.id ? "#0d1833" : "#fcb31f",
              fontSize: "0.7rem", fontWeight: 800,
              padding: "0.1rem 0.4rem", borderRadius: "999px", marginLeft: "0.1rem",
            }}>{t.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// "3" → "3rd" — for the deviceRequestSeq line on queue cards.
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

// Format elapsed milliseconds as a human wait time: "45m" or "2h 10m".
function fmtWait(ms) {
  if (ms == null || ms < 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

// Downscale an already-native (JPEG/PNG) image to a max edge, PRESERVING its
// aspect ratio, and return a JPEG blob. Used by the "Full piece" content path
// so wide reference art (e.g. Nighthawks) uploads whole — no square crop. Run
// the file through normalizePhotoForUpload first so iPhone HEIC decodes.
async function downscaleToJpeg(file, maxEdge = 1920, quality = 0.9) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", quality));
}

export default function ArtistQueueView({ user, navigate }) {
  const [orders, setOrders]                   = useState([]);
  const [studioOrders, setStudioOrders]       = useState([]);
  const [stagedOrders, setStagedOrders]       = useState([]);       // pre-uploads (booking-bound)
  const [stagedShiftOrders, setStagedShiftOrders] = useState([]);   // party-list rows (shift-bound)
  const [promotingOrderId, setPromotingOrderId] = useState(null);
  const [promotingStandbyIds, setPromotingStandbyIds] = useState([]); // standby cards with an in-flight promote
  const [stagingTrayOpen, setStagingTrayOpen]   = useState(false);  // collapsed by default — preserves queue visibility when many orders are staged
  const [showPartyList, setShowPartyList]       = useState(false);  // 📋 batch-add modal
  const [partyListText, setPartyListText]       = useState("");
  const [partyListSubmitting, setPartyListSubmitting] = useState(false);
  const [attachTargetId, setAttachTargetId]     = useState(null);   // staged order awaiting an image pick
  const [queueTab, setQueueTab]               = useState(null); // null = auto-detect, "live" | "studio"
  const [showNewContent, setShowNewContent]   = useState(false);
  const [newContentName, setNewContentName]   = useState("");
  const [newContentImageFile, setNewContentImageFile] = useState(null);
  const [newContentCroppedBlob, setNewContentCroppedBlob] = useState(null);
  const [newContentFullPiece, setNewContentFullPiece] = useState(false); // skip circle crop — upload whole image (wide art)
  const [newContentSubmitting, setNewContentSubmitting] = useState(false);
  const newContentFileInputRef = useRef(null);
  // Object URL for the "Full piece" whole-image preview (FramingTool makes its
  // own URL for the circle-crop path, so this is only used when full-piece is on).
  const newContentPreviewUrl = useMemo(
    () => (newContentImageFile ? URL.createObjectURL(newContentImageFile) : null),
    [newContentImageFile]
  );
  useEffect(() => () => { if (newContentPreviewUrl) URL.revokeObjectURL(newContentPreviewUrl); }, [newContentPreviewUrl]);
  // Studio drag-to-reorder state. dragRef holds the per-gesture working data
  // (timer, start coords, etc) so we don't trigger renders on every move; we
  // only update React state once we've actually entered drag mode.
  const [studioDrag, setStudioDrag] = useState(null);
  const studioDragRef = useRef(null);
  const [selectedOrder, setSelectedOrder]     = useState(null);
  // Standby promote is armed-then-confirmed (two taps) — a single accidental
  // tap on a standby card must never irreversibly burn a queue number.
  const [armedStandbyId, setArmedStandbyId]   = useState(null);
  useEffect(() => {
    if (!armedStandbyId) return;
    const t = setTimeout(() => setArmedStandbyId(null), 5000); // auto-disarm
    return () => clearTimeout(t);
  }, [armedStandbyId]);
  const [mirrored, setMirrored]               = useState(true);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [activeShift, setActiveShift]         = useState(null);
  const [shiftLoading, setShiftLoading]       = useState(true);
  // Operator explicitly asked to open a SECOND concurrent queue. Needed
  // because "one shift is open" is indistinguishable from "my shift is open"
  // — Wella's iPad would otherwise resolve to Dan's queue and never offer to
  // start her own. Intent has to be declared, not inferred.
  const [openAnother, setOpenAnother]         = useState(false);
  // Re-open the booth picker on demand from the header chip.
  const [showShiftPicker, setShowShiftPicker] = useState(false);
  // Pass activeShift so per-event flash boards (activeFlashProfileIds) resolve
  // in designMap + the reference picker — without it only the base IP set loads.
  const { designs: flashDesigns, designMap } = useFlashDesigns(activeShift);
  // Flash reference picker — opens a design as a VIEW-ONLY synthetic order
  // (no Firestore doc, no token, no queue number). See makeFlashRefOrder().
  const [showFlashRefs, setShowFlashRefs]     = useState(false);
  const [grid, setGrid]                       = useState(DEFAULT_GRID);
  const [showGridOptions, setShowGridOptions] = useState(false);
  const [showUI, setShowUI]                   = useState(true);
  const imgRef                = useRef(null);
  const outerContainerRef     = useRef(null);   // touch event target — full-screen div
  const touchContainerRef     = useRef(null);   // image area — cx/cy pivot for pinch only
  const gestureRef            = useRef(null);
  const xformRef              = useRef({ scale: 1, tx: 0, ty: 0, rotation: 0 });   // image transform (rotation in degrees)
  const gridXformRef          = useRef({ scale: 1, tx: 0, ty: 0 });   // grid transform (independent; no rotation — grid aligns to the griddle, not the subject)
  const gridOverlayRef        = useRef(null);                          // grid DOM element
  const gridLockedRef         = useRef(true);                          // read by touch handlers
  const gestureWrapperRef     = useRef(null);
  const mirrorWrapperRef      = useRef(null);
  const ordersRef             = useRef(orders);
  const [imgSize, setImgSize] = useState(null);
  const [xform, setXform]     = useState({ scale: 1, tx: 0, ty: 0, rotation: 0 });
  // Twist-to-rotate is OPT-IN. Default off so routine zooming can't
  // accidentally tilt the reference — rotation is a rare correction for
  // sideways uploads, turned on only when needed. Snap-to-15° is implied
  // whenever twist-rotate is active (no separate toggle — user who opts in
  // wants the cleanup).
  const [twistRotate, setTwistRotate] = useState(false);
  const twistRotateRef = useRef(twistRotate);
  twistRotateRef.current = twistRotate;
  const [gridLocked, setGridLocked] = useState(true);
  // Per-order image state cache: { [orderId]: { xform, gridXform, mirrored, gridLocked } }
  const orderStateCache = useRef({});
  const mirroredRef = useRef(mirrored);
  mirroredRef.current = mirrored;
  const gridSideRef = useRef(grid.side);
  gridSideRef.current = grid.side;
  const fullGriddleRef = useRef(grid.fullGriddle);
  fullGriddleRef.current = grid.fullGriddle;
  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);
  const [now, setNow]                 = useState(Date.now());

  // ── ARTIST STATE ─────────────────────────────────────────────────────────
  const [showFinishWithoutPhotoConfirm, setShowFinishWithoutPhotoConfirm] = useState(false);
  const [showStillDrawing, setShowStillDrawing]   = useState(false);
  const [flipCountdown, setFlipCountdown] = useState(null); // null | Date.now() timestamp
  // Notes acknowledgment: { order, artist } pending start after dismiss
  const [pendingNoteAck, setPendingNoteAck] = useState(null);
  const [reissueOrder, setReissueOrder] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showRejected, setShowRejected]   = useState(false);
  const [rejectedOrders, setRejectedOrders] = useState([]);
  // Mood check — fires once per shift before the first pancake starts
  const moodCheckDone = useRef(false);
  const [pendingMoodCheck, setPendingMoodCheck] = useState(null); // { order, artist } awaiting mood submit
  const [moodSelections, setMoodSelections] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  gridLockedRef.current = gridLocked;
  const [extractedColors,  setExtractedColors]    = useState([]);
  const [isolatedColorIdx, setIsolatedColorIdx]   = useState(null);
  const [isolationOverlay, setIsolationOverlay]   = useState(null);
  const stillDrawingDismissedAt = useRef(null); // timestamp when last dismissed
  const [activeArtist, setActiveArtistRaw] = useState(() => localStorage.getItem("gt_active_artist") || "Dan");
  const [artists, setArtistsState]         = useState(() => {
    try { return JSON.parse(localStorage.getItem("gt_artists")) || ["Dan"]; }
    catch { return ["Dan"]; }
  });

  function setActiveArtist(name) {
    setActiveArtistRaw(name);
    localStorage.setItem("gt_active_artist", name);
  }

  // Artists registry — resolves free-text names to stable ids for the
  // order.artistId stamp (artistName stays the display string).
  const { byName: artistsByName } = useArtists();

  // Keep artists in sync with localStorage. "storage" only fires from OTHER
  // documents; ShiftManager (mounted in this same document) dispatches the
  // custom "gt-artists-changed" event so mid-shift roster edits show up on
  // the start scrim without a reload.
  useEffect(() => {
    function sync() {
      try { setArtistsState(JSON.parse(localStorage.getItem("gt_artists")) || ["Dan"]); }
      catch { /* ignore */ }
    }
    window.addEventListener("storage", sync);
    window.addEventListener("gt-artists-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("gt-artists-changed", sync);
    };
  }, []);

  // ── PAST SHIFTS ───────────────────────────────────────────────────────────
  const [pastShifts, setPastShifts] = useState([]);
  const [pastShiftsLoading, setPastShiftsLoading] = useState(false);

  useEffect(() => {
    if (activeShift) return;
    setPastShiftsLoading(true);
    getDocs(query(
      collection(db, "shift_summaries"),
      orderBy("closedAt", "desc"),
      limit(10)
    ))
      .then(snap => setPastShifts(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setPastShiftsLoading(false));
  }, [activeShift]);

  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // New-order sound is handled globally in App.js.

  // Tick every 100ms — drives live stopwatches (tenths precision) and card wait times.
  // Also checks for "still drawing?" prompt: fires after 10min of drawing with no dismiss.
  useEffect(() => {
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      // "Still drawing?" — only in full-screen drawing view
      if (selectedOrder?.state === "drawing" && selectedOrder?.startedAt) {
        const drawMs = t - selectedOrder.startedAt.toMillis();
        const lastDismiss = stillDrawingDismissedAt.current ?? 0;
        if (drawMs > 10 * 60 * 1000 && t - lastDismiss > 10 * 60 * 1000) {
          setShowStillDrawing(true);
        }
      }
    }, 100);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder?.id, selectedOrder?.state, selectedOrder?.startedAt]);

  useEffect(() => {
    function check() { setIsLandscape(window.innerWidth > window.innerHeight); }
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  // Shift scoping (2026-09-25). Was a local isActive→docs[0] query, which
  // coin-flipped the moment a second event opened its own queue. The resolver
  // in useActiveShift owns that decision now; `activeShift` stays local state
  // so the rest of this view (and ShiftManager's optimistic open/close
  // callbacks) is untouched.
  const {
    shift: scopedShift,
    loading: scopeLoading,
    candidates: activeCandidates,
    needsChoice: needsShiftChoice,
    isConcurrent,
    bind: bindShift,
  } = useActiveShift();

  useEffect(() => {
    setActiveShift(scopedShift);
    setShiftLoading(scopeLoading);
  }, [scopedShift, scopeLoading]);

  // Clear per-order image state cache and mood check when shift ends
  useEffect(() => {
    if (!activeShift) { orderStateCache.current = {}; moodCheckDone.current = false; }
    // If shift already has a mood check recorded, skip it
    if (activeShift?.moodCheck) moodCheckDone.current = true;
  }, [activeShift]);

  useEffect(() => {
    if (!activeShift) { setOrders([]); setLoading(false); return; }
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "in", ["queued", "drawing", "cooking", "flipping", "suspended", "ready_to_deliver"])
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orderList = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
      // Main queue sorts by queueNumber — the number IS the position (it's what
      // the guest holds and what the overlay shows). Unnumbered orders go to the
      // BACK, never the head: standby docs deliberately have no submittedAt, so
      // the old submittedAt sort left them effectively unsorted and let manual
      // promotes (which lacked submittedAt too) jump to the front. Standby is
      // its own second-tier FCFS line, ordered by request time (createdAt).
      orderList.sort((a, b) => {
        const aStandby = a.queue === "standby";
        const bStandby = b.queue === "standby";
        if (aStandby !== bStandby) return aStandby ? 1 : -1;
        if (aStandby) return (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0);
        const an = a.queueNumber ?? Infinity;
        const bn = b.queueNumber ?? Infinity;
        if (an !== bn) return an - bn;
        return (a.submittedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)
             - (b.submittedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0);
      });
      setOrders(orderList);
      setLoading(false);
      // Functional update so React's pending state is respected — if markDone/rejectOrder
      // already queued setSelectedOrder(null), prev will be null here and we won't re-open.
      setSelectedOrder(prev => {
        if (!prev) return null;
        return orderList.find(o => o.id === prev.id) ?? prev;
      });
    });
    return () => unsubscribe();
  }, [activeShift?.id, selectedOrder?.id]);

  // Studio orders — commissions, content pieces, pre-orders. Always loaded
  // regardless of shift state. These are the async backlog.
  useEffect(() => {
    const q = query(
      collection(db, "orders"),
      where("fulfillmentMode", "==", "studio"),
      where("state", "in", ["queued", "drawing", "cooking", "flipping", "suspended", "ready_to_deliver"])
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => !o.trashedAt)
        .sort((a, b) => (a.sortIndex ?? 9999) - (b.sortIndex ?? 9999) || (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
      setStudioOrders(list);
      // Keep selectedOrder in sync for studio orders too — without this, tapping
      // Start on a content piece with no shiftId updates Firestore but the local
      // selectedOrder ref stays stale, so the button doesn't visually advance.
      setSelectedOrder(prev => {
        if (!prev) return null;
        const fresh = list.find(o => o.id === prev.id);
        return fresh ?? prev;
      });
    });
    return () => unsub();
  }, []);

  // Staged pre-uploads — orders the client uploaded ahead of the event
  // and bound to this booking. Sit in their own tray above the live queue
  // until staff taps Promote (calls promoteStagedOrder server-side, which
  // assigns queueNumber + designSequence atomically). When the active
  // shift isn't bound to a booking, there's nothing to stage.
  useEffect(() => {
    if (!activeShift?.bookingId) { setStagedOrders([]); return; }
    const q = query(
      collection(db, "orders"),
      where("bookingId", "==", activeShift.bookingId),
      where("state", "==", "staged")
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => !o.trashedAt)
        .sort((a, b) => (a.stagedAt?.toMillis?.() ?? 0) - (b.stagedAt?.toMillis?.() ?? 0));
      setStagedOrders(list);
    });
    return () => unsub();
  }, [activeShift?.bookingId]);

  // Party-list staged orders — staff-batched guests bound to THIS shift (no
  // booking needed). Same tray + promote path as pre-uploads; merged and
  // deduped with the booking-bound list at render (a party-list row also
  // stamps bookingId when the shift has one, so both listeners can see it).
  useEffect(() => {
    if (!activeShift?.id) { setStagedShiftOrders([]); return; }
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "==", "staged")
    );
    const unsub = onSnapshot(q, (snap) => {
      setStagedShiftOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt));
    });
    return () => unsub();
  }, [activeShift?.id]);

  // Completed orders — only fetched when toggle is on
  useEffect(() => {
    if (!activeShift || !showCompleted) { setCompletedOrders([]); return; }
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "==", "done")
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
      list.sort((a, b) => (b.completedAt?.toMillis?.() ?? 0) - (a.completedAt?.toMillis?.() ?? 0));
      setCompletedOrders(list);
    });
    return () => unsub();
  }, [activeShift?.id, showCompleted]);

  // Rejected orders — only fetched when toggle is on. The live query excludes
  // "rejected", so without this list a mis-tapped reject was invisible until
  // the shift archive (all data intact — reference image, name, notes).
  useEffect(() => {
    if (!activeShift || !showRejected) { setRejectedOrders([]); return; }
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "==", "rejected")
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
      list.sort((a, b) => (b.rejectedAt?.toMillis?.() ?? 0) - (a.rejectedAt?.toMillis?.() ?? 0));
      setRejectedOrders(list);
    });
    return () => unsub();
  }, [activeShift?.id, showRejected]);

  // Measure the gesture wrapper (not the image) so imgSize always equals the circle diameter
  // regardless of whether we're showing croppedImageUrl or a full-size positioned imageUrl.
  // Also compensate pan (tx/ty) when the container resizes — toggling the HUD or grid panel
  // changes the wrapper height, and the CSS translate is in absolute pixels. Without the
  // proportional adjustment below, Dan's zoom/pan appears to "jut" sideways by the delta.
  const lastWrapperSizeRef = useRef(null);
  useEffect(() => {
    if (!selectedOrder) return;
    function measure() {
      const wrapper = gestureWrapperRef.current;
      if (!wrapper) return;
      const size = Math.round(Math.min(wrapper.offsetWidth, wrapper.offsetHeight));
      // Re-scale tx/ty by the size ratio so the pan stays at the same
      // fraction of the (now differently-sized) container. Skip when the
      // image isn't zoomed/panned — nothing to compensate.
      const prev = lastWrapperSizeRef.current;
      if (prev && prev !== size && (xformRef.current.tx || xformRef.current.ty)) {
        const ratio = size / prev;
        const adj = {
          ...xformRef.current,
          tx: xformRef.current.tx * ratio,
          ty: xformRef.current.ty * ratio,
        };
        xformRef.current = adj;
        setXform(adj);
        if (gestureWrapperRef.current)
          gestureWrapperRef.current.style.transform = `translate(${adj.tx}px, ${adj.ty}px) rotate(${adj.rotation || 0}deg) scale(${adj.scale})`;
      }
      lastWrapperSizeRef.current = size;
      setImgSize({ size });
    }
    // Delay initial measure by one frame so the browser has reflowed after showUI toggle
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [selectedOrder, isLandscape, showUI]); // showGridOptions no longer resizes the wrapper — submenus overlay now

  // Save/restore zoom/pan/mirror per order when switching; reset drawing prompts
  const prevOrderIdRef = useRef(null);
  useEffect(() => {
    const DEFAULT_XFORM = { scale: 1, tx: 0, ty: 0, rotation: 0 };
    // Save outgoing order's image state
    const prevId = prevOrderIdRef.current;
    if (prevId) {
      orderStateCache.current[prevId] = {
        xform: { ...xformRef.current },
        gridXform: { ...gridXformRef.current },
        mirrored: mirroredRef.current,
        gridLocked: gridLockedRef.current,
        gridSide: gridSideRef.current,
        fullGriddle: fullGriddleRef.current,
      };
    }
    prevOrderIdRef.current = selectedOrder?.id ?? null;
    // Restore cached state or reset to defaults
    const cached = selectedOrder?.id ? orderStateCache.current[selectedOrder.id] : null;
    const imgXf   = cached?.xform     ?? DEFAULT_XFORM;
    const gridXf  = cached?.gridXform ?? DEFAULT_XFORM;
    xformRef.current = imgXf;
    gridXformRef.current = gridXf;
    setXform(imgXf);
    if (cached) {
      setMirrored(cached.mirrored);
      setGridLocked(cached.gridLocked);
      setGrid(g => ({ ...g, side: cached.gridSide ?? null, fullGriddle: cached.fullGriddle ?? false }));
    } else {
      setGridLocked(true);
      setGrid(g => ({ ...g, side: null, fullGriddle: false }));
    }
    if (gestureWrapperRef.current) {
      gestureWrapperRef.current.style.transform = `translate(${imgXf.tx}px, ${imgXf.ty}px) rotate(${imgXf.rotation || 0}deg) scale(${imgXf.scale})`;
    }
    if (gridOverlayRef.current) {
      gridOverlayRef.current.style.transform = `translate(${gridXf.tx}px, ${gridXf.ty}px) scale(${gridXf.scale})`;
    }
    setShowStillDrawing(false);
    stillDrawingDismissedAt.current = null;
    setExtractedColors([]);
    setIsolatedColorIdx(null);
    setIsolationOverlay(null);
  }, [selectedOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract dominant colors from reference image
  useEffect(() => {
    if (!selectedOrder) return;
    const imgUrl = selectedOrder.imageUrl || selectedOrder.croppedImageUrl;
    if (!imgUrl) return;
    let cancelled = false;
    extractDominantColors(imgUrl, 7).then(colors => {
      if (!cancelled) setExtractedColors(colors);
    });
    return () => { cancelled = true; };
  }, [selectedOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleColorTap = useCallback(async (idx) => {
    if (isolatedColorIdx === idx) {
      // Deselect
      setIsolatedColorIdx(null);
      setIsolationOverlay(null);
      return;
    }
    setIsolatedColorIdx(idx);
    const imgUrl = selectedOrder?.imageUrl || selectedOrder?.croppedImageUrl;
    if (!imgUrl) return;
    const overlay = await generateColorIsolation(imgUrl, extractedColors[idx]);
    setIsolationOverlay(overlay);
  }, [isolatedColorIdx, selectedOrder, extractedColors]);

  // Pinch-to-zoom, pan, and swipe.
  // Image + grid have independent transforms. When locked, gestures apply to both.
  // When unlocked, only the image moves — grid stays frozen.
  // Re-locking preserves both positions; they resume moving together.
  useEffect(() => {
    if (!selectedOrder) return;
    const el = outerContainerRef.current;
    if (!el) return;

    function getDist(t1, t2) {
      return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    }
    function getMid(t1, t2) {
      return { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
    }
    function applyImg(next) {
      xformRef.current = next;
      if (gestureWrapperRef.current)
        gestureWrapperRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) rotate(${next.rotation || 0}deg) scale(${next.scale})`;
    }
    function applyGrid(next) {
      gridXformRef.current = next;
      if (gridOverlayRef.current)
        gridOverlayRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) scale(${next.scale})`;
    }
    function computePinch(start, newDist, startDist, startMid, newMid, cx, cy, rotationDelta) {
      const rawScale = start.scale * (newDist / startDist);
      const s = Math.max(0.05, Math.min(4, rawScale));
      const r = s / start.scale;
      const out = { scale: s, tx: start.tx * r + (startMid.x - cx) * (1 - r) + (newMid.x - startMid.x), ty: start.ty * r + (startMid.y - cy) * (1 - r) + (newMid.y - startMid.y) };
      if (rotationDelta != null) {
        const prevRot = start.rotation || 0;
        // Rotation direction is inverted from the raw math so the image tracks
        // the same way Dan's fingers do on the iPad — without this it feels
        // like turning a crank (fingers twist one way, image goes the other).
        // Mirror state flips the apparent direction back, so the sign swaps.
        const signed = mirroredRef.current ? rotationDelta : -rotationDelta;
        let next = prevRot + signed;
        next = ((next + 180) % 360 + 360) % 360 - 180;
        out.rotation = next;
      } else {
        // Preserve whatever rotation was already on the image. Without this,
        // the returned object has no `rotation` key, applyImg overwrites
        // xformRef.current with the partial object, and the rotation silently
        // disappears the instant a second finger lands to start a pinch.
        out.rotation = start.rotation || 0;
      }
      return out;
    }

    function onTouchStart(e) {
      // Don't intercept touches on UI controls (sliders, checkboxes, color pickers, etc.)
      if (e.target.closest("input, select, textarea, label")) return;

      const pivotEl = touchContainerRef.current ?? el;
      const rect    = pivotEl.getBoundingClientRect();
      const cx      = rect.left + rect.width  / 2;
      const cy      = rect.top  + rect.height / 2;
      if (e.touches.length >= 2) {
        e.preventDefault();
        const dx0 = e.touches[1].clientX - e.touches[0].clientX;
        const dy0 = e.touches[1].clientY - e.touches[0].clientY;
        gestureRef.current = {
          type: "pinch",
          startDist:  getDist(e.touches[0], e.touches[1]),
          startMid:   getMid(e.touches[0],  e.touches[1]),
          startAngle: (Math.atan2(dy0, dx0) * 180) / Math.PI,
          imgStart:   { ...xformRef.current },
          grdStart:   { ...gridXformRef.current },
          cx, cy,
        };
      } else if (e.touches.length === 1) {
        gestureRef.current = {
          type: "pan",
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          imgStart:  { ...xformRef.current },
          grdStart:  { ...gridXformRef.current },
          startTime: Date.now(),
          orderId:   selectedOrder?.id,
        };
      }
    }

    function onTouchMove(e) {
      const g = gestureRef.current;
      if (!g) return;
      e.preventDefault();
      if (g.type === "pinch" && e.touches.length >= 2) {
        const newDist = getDist(e.touches[0], e.touches[1]);
        const newMid  = getMid(e.touches[0],  e.touches[1]);
        // Only compute rotation delta when the user has opted in. Otherwise
        // two-finger gestures are strictly zoom/pan — the image won't tilt
        // from stray twist during routine pinch-zoom.
        let rotDelta = null;
        if (twistRotateRef.current) {
          const dx1 = e.touches[1].clientX - e.touches[0].clientX;
          const dy1 = e.touches[1].clientY - e.touches[0].clientY;
          const newAngle = (Math.atan2(dy1, dx1) * 180) / Math.PI;
          // Shortest-arc delta so crossing ±180° doesn't flip the image.
          rotDelta = newAngle - g.startAngle;
          if (rotDelta >  180) rotDelta -= 360;
          if (rotDelta < -180) rotDelta += 360;
        }
        applyImg(computePinch(g.imgStart, newDist, g.startDist, g.startMid, newMid, g.cx, g.cy, rotDelta));
        // Grid stays aligned to the griddle — no rotation applied.
        if (gridLockedRef.current)
          applyGrid(computePinch(g.grdStart, newDist, g.startDist, g.startMid, newMid, g.cx, g.cy, null));
      } else if (g.type === "pan" && e.touches.length === 1) {
        const dx = e.touches[0].clientX - g.startX;
        const dy = e.touches[0].clientY - g.startY;
        applyImg({ ...g.imgStart, tx: g.imgStart.tx + dx, ty: g.imgStart.ty + dy });
        if (gridLockedRef.current)
          applyGrid({ ...g.grdStart, tx: g.grdStart.tx + dx, ty: g.grdStart.ty + dy });
      }
    }

    function onTouchEnd() {
      // Snap rotation to 15° whenever twist-rotate produced a non-aligned
      // angle. Only fires when the opt-in toggle is on AND rotation is
      // non-zero — zoom/pan gestures leave rotation at zero so this is a
      // no-op for the common case.
      if (twistRotateRef.current && xformRef.current.rotation) {
        const step = 15;
        const snapped = Math.round(xformRef.current.rotation / step) * step;
        const next = ((snapped + 180) % 360 + 360) % 360 - 180;
        if (next !== xformRef.current.rotation) {
          applyImg({ ...xformRef.current, rotation: next });
        }
      }
      gestureRef.current = null;
      setXform({ ...xformRef.current });
    }

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, [selectedOrder?.id]);

  async function promoteFromStandby(order) {
    if (!activeShift) return;
    if (promotingStandbyIds.includes(order.id)) return;
    // Card shows "Promoting…" and locks IMMEDIATELY — the callable can take
    // several cold-start seconds and the old zero-feedback wait read as a
    // dead button, prompting re-taps.
    setPromotingStandbyIds(ids => [...ids, order.id]);
    try {
      // Server callable → promoteStandbyOrderToMain: counter increment + order
      // write in ONE transaction (same path as webhook/verify/sweep promotes).
      // It also stamps submittedAt at promotion time, so the promoted order
      // sorts correctly everywhere. The old client-side version was two writes;
      // if the second failed, a queue number was burned and the order stayed
      // on standby.
      const fn = httpsCallable(getFunctions(getApp()), "promoteStandbyOrder");
      await fn({ orderId: order.id, shiftId: activeShift.id });
    } catch (err) {
      console.error("Promote failed:", err);
      alert("Promote failed: " + (err.message || "Unknown error. Try again."));
    } finally {
      setPromotingStandbyIds(ids => ids.filter(x => x !== order.id));
    }
  }

  // Batch-promote every waiting standby order (server loops the same
  // idempotent transaction). Party flips get offered this from Shift
  // Controls; this button covers markets and anyone the flip prompt missed.
  async function admitAllFromStandby(count) {
    if (!activeShift?.id) return;
    if (!window.confirm(`Admit all ${count} standby request${count === 1 ? "" : "s"} into the queue?`)) return;
    try {
      const fn = httpsCallable(getFunctions(getApp()), "admitAllStandby");
      await fn({ shiftId: activeShift.id });
    } catch (err) {
      console.error("Admit all failed:", err);
      alert("Admit all failed: " + (err?.message || "Unknown error. Try again."));
    }
  }

  // 📋 Party list — batch-add guests to the staging tray. One line per guest,
  // "Name — what they want" (leading list numbers tolerated). Born staged so
  // you can google/attach reference images from each row's ⋯ menu and promote
  // in party order — replaces hand-entering each kid as a one-off order.
  async function submitPartyList() {
    if (!activeShift?.id || partyListSubmitting) return;
    const lines = partyListText
      .split("\n")
      .map(l => l.replace(/^\s*\d+\s*[.)]?\s*/, "").trim())
      .filter(Boolean);
    if (!lines.length) return;
    setPartyListSubmitting(true);
    try {
      for (const line of lines) {
        const m = line.match(/^(.*?)\s*[-–—:]\s+(.+)$/);
        const name = ((m ? m[1] : line) || "").trim() || "Guest";
        const note = m ? m[2].trim() : null;
        await addDoc(collection(db, "orders"), {
          state:         "staged",
          source:        "party_list",
          isPreUpload:   false,
          orderType:     "custom",
          recipientName: name,
          customerName:  name,
          // The request IS the pancake's subject — "Mia — unicorn" titles the
          // pancake "unicorn". subjectName is the only non-PII title source
          // for customs (publicGallery never reads recipientName), so without
          // it every party-list pancake published as "Untitled".
          ...(note ? { notes: note.slice(0, 200), subjectName: note.slice(0, 120) } : {}),
          shiftId:       activeShift.id,
          bookingId:     activeShift.bookingId || null,
          queue:         null,
          queueNumber:   null,
          stagedAt:      serverTimestamp(),
          createdAt:     serverTimestamp(),
          // Test-data rule: every creation path inherits the shift's flag.
          ...(activeShift._isTestData ? { _isTestData: true } : {}),
        });
      }
      setPartyListText("");
      setShowPartyList(false);
      setStagingTrayOpen(true);
    } catch (err) {
      console.error("Party list failed:", err);
      alert("Party list failed: " + (err?.message || "Unknown error."));
    }
    setPartyListSubmitting(false);
  }

  // Attach/replace a reference image on an order. Two workflows share this:
  // party-list staged rows, and (2026-09-25) freestyle requests already in the
  // queue — when the griddle is quiet enough to go find a picture of what the
  // guest asked for. Whole image, no crop; the artist view pans/zooms it like
  // other full-piece customs, and `imageUrl` is what that view already reads,
  // so attaching one simply turns the text card into a reference card.
  const attachInputRef = useRef(null);
  function requestAttachImage(order) {
    setAttachTargetId(order.id);
    attachInputRef.current?.click();
  }
  async function handleAttachImageFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const targetId = attachTargetId;
    setAttachTargetId(null);
    if (!file || !targetId) return;
    try {
      const { file: normalized, error } = await normalizePhotoForUpload(file);
      if (error) { alert(error); return; }
      const blob = await downscaleToJpeg(normalized);
      const path = `orders/${targetId}/full_${Date.now()}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: "image/jpeg" });
      const url = await getDownloadURL(ref);
      await updateDoc(doc(db, "orders", targetId), { imageUrl: url, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error("Attach image failed:", err);
      alert("Image upload failed: " + (err?.message || "Unknown error."));
    }
  }

  // Undo a reference attach — a rushed grab off Google is easy to regret, and
  // without this the only way back to the plain request text is a better
  // image. Deletes the upload too: nulling the field alone would orphan it,
  // since the order-delete sweep finds storage objects VIA these url fields.
  async function detachReferenceImage(order) {
    if (!order?.imageUrl) return;
    const url = order.imageUrl;
    // Detach FIRST. Removing the reference is what the artist asked for;
    // deleting the upload is housekeeping and must never be able to block it.
    // (storageRef() THROWS synchronously on a url outside our bucket — e.g. a
    // commission image copied in from elsewhere — so cleanup-first meant those
    // references could never be removed at all.)
    try {
      await updateDoc(doc(db, "orders", order.id), { imageUrl: null, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error("Detach reference failed:", err);
      alert("Couldn't remove that reference: " + (err?.message || "Unknown error."));
      return;
    }
    // Best effort: an orphaned file is a rounding error, a blocked artist is not.
    try {
      await deleteObject(storageRef(storage, url));
    } catch (err) {
      console.warn("Reference image detached but not deleted from storage:", err?.message || err);
    }
  }

  // Promote a staged pre-upload into the live queue. Server-side callable
  // handles the atomic queueNumber + designSequence assignment + state flip.
  async function promoteStagedOrder(order) {
    if (!activeShift?.id) return;
    if (promotingOrderId) return;
    setPromotingOrderId(order.id);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "promoteStagedOrder");
      await fn({ orderId: order.id, shiftId: activeShift.id });
      // No local state update — onSnapshot drops it from stagedOrders and
      // surfaces it in the live orders list as state flips to "queued".
    } catch (err) {
      console.error("Promote staged failed:", err);
      alert("Promote failed: " + (err?.message || "Unknown error. Try again."));
    } finally {
      setPromotingOrderId(null);
    }
  }

  async function trashStagedOrder(order) {
    if (!window.confirm(`Move "${order.recipientName || "this pre-upload"}" to trash?\n\nIt'll stay recoverable for 30 days.`)) return;
    try {
      await updateDoc(doc(db, "orders", order.id), { trashedAt: serverTimestamp() });
    } catch (err) {
      console.error("Trash staged failed:", err);
      alert("Trash failed: " + (err?.message || "Unknown error."));
    }
  }

  async function toggleStagedHold(order) {
    try {
      await updateDoc(doc(db, "orders", order.id), { holdIndefinitely: !order.holdIndefinitely });
    } catch (err) {
      console.error("Toggle hold failed:", err);
      alert("Update failed: " + (err?.message || "Unknown error."));
    }
  }

  async function startOrder(order, artistName) {
    const artist = artistName ?? activeArtist;
    const isStudioPull = order.fulfillmentMode === "studio" && !order.shiftId && activeShift;

    // Studio order being pulled into an active session — assign shiftId + queueNumber
    const updates = {
      state: "drawing",
      startedAt: serverTimestamp(),
      artistName: artist,
      // Stable registry identity alongside the display string — null when the
      // name has no registry entry yet (attribution still works by name).
      artistId: artistsByName[artist.trim().toLowerCase()]?.id ?? null,
      eventName:     activeShift?.eventName     ?? null,
      venueAddress:  activeShift?.venueAddress  ?? null,
      venueIsPublic: activeShift?.venueIsPublic !== false,
    };

    if (isStudioPull) {
      // Assign a queue number via the same atomic counter as booth orders
      try {
        const num = await assignQueueNumber(activeShift.id);
        updates.shiftId = activeShift.id;
        updates.queueNumber = num;
        updates.queue = null; // remove from standby if it was there
      } catch (e) {
        // Fallback: just stamp the shiftId without a queue number
        updates.shiftId = activeShift.id;
        console.warn("startOrder: queue number assignment failed for studio pull", e.message);
      }
      // Copy commission reference image to imageUrl so the artist view picks it up
      if (order.commissionImageUrl && !order.imageUrl) {
        updates.imageUrl = order.commissionImageUrl;
      }
    }

    if (activeShift && (order.queueNumber || updates.queueNumber)) {
      await updateDoc(doc(db, "shifts", activeShift.id), { currentNumber: order.queueNumber || updates.queueNumber });
    }
    await updateDoc(doc(db, "orders", order.id), updates);
    setActiveArtist(artist);
    setMirrored(true);
  }

  async function markCooking(order) {
    const drawingTimeMs = order.startedAt ? Date.now() - order.startedAt.toDate().getTime() : null;
    await updateDoc(doc(db, "orders", order.id), { state: "cooking", cookingStartedAt: serverTimestamp(), drawingTimeMs });
    // Track shift best draw time when pancake goes on the griddle
    if (activeShift && drawingTimeMs != null) {
      const isCustom = order.orderType === "custom";
      const drawKey  = isCustom ? "shiftBestCustomDrawMs" : "shiftBestFlashDrawMs";
      const updates  = {};
      if ((activeShift[drawKey] ?? Infinity) > drawingTimeMs) updates[drawKey] = drawingTimeMs;
      // Feed the shift tempo (drives every wait estimate): sample = actual
      // draw ÷ type baseline, rolling last-N window. Skip absurd durations —
      // a mis-tap or a pancake that sat suspended would burn a window slot.
      if (drawingTimeMs >= 30 * 1000 && drawingTimeMs <= 45 * 60 * 1000) {
        const sample = drawingTimeMs / (baselineWaitMinutes(order) * 60000);
        updates.drawTempoSamples = [...(activeShift.drawTempoSamples ?? []), sample].slice(-TEMPO_SAMPLE_WINDOW);
      }
      if (Object.keys(updates).length > 0)
        updateDoc(doc(db, "shifts", activeShift.id), updates).catch(() => {});
    }
    setMirrored(false);
  }

  async function markFlipping(order) {
    // Trigger the flip countdown on the artist view — store start time so the
    // time-based countdown stays in sync with the overlay's animation.
    setFlipCountdown(Date.now());
    await updateDoc(doc(db, "orders", order.id), { state: "flipping", flippedAt: serverTimestamp() });
    // Clear currentNumber + bump lastActivityAt for auto-close inactivity timer
    if (activeShift) {
      const shiftUpdates = { lastActivityAt: serverTimestamp() };
      // Keep currentNumber at the completed order's number (not 0) so customer wait
      // estimates stay accurate. Resetting to 0 inflates the gap for the next customer.
      updateDoc(doc(db, "shifts", activeShift.id), shiftUpdates).catch(() => {});
    }
    setMirrored(false);
  }

  // Lock the cook time the instant the pancake comes off the griddle, decoupled
  // from when (or whether) it's photographed — the photo/upload used to end the
  // clock, so framing + review + upload latency inflated every cook number.
  // Idempotent: first write wins (the /camera shutter locks it too), so a later
  // tap here never overwrites an already-captured value.
  async function markOffGriddle(order) {
    if (order.cookTimeMs != null) return; // already locked here or by the camera
    const cookStart  = order.cookingStartedAt || order.flippedAt;
    const cookTimeMs = cookStart?.toMillis ? Date.now() - cookStart.toMillis() : null;
    await updateDoc(doc(db, "orders", order.id), {
      ...(cookTimeMs != null ? { cookTimeMs } : {}),
      cookEndAt: serverTimestamp(),
    });
  }

  async function suspendOrder(order) {
    await updateDoc(doc(db, "orders", order.id), {
      state:              "suspended",
      suspendedAt:        serverTimestamp(),
      suspendedFromState: order.state,
      // Staff suspensions stay suspended until staff resume them —
      // revertExpiredSuspensions (the 3-min customer "switch images" auto-
      // revert) skips them on this flag. Without it, a suspended absent
      // guest silently re-enters the queue behind Dan's back.
      suspendedBy:        "staff",
      suspensionCount:    (order.suspensionCount ?? 0) + 1,
    });
    if (activeShift) {
      await updateDoc(doc(db, "shifts", activeShift.id), {
        suspendedCount: (activeShift.suspendedCount ?? 0) + 1,
      });
    }
  }

  async function resumeOrder(order) {
    await updateDoc(doc(db, "orders", order.id), {
      state:          order.suspendedFromState ?? "drawing",
      resubmittedAt:  serverTimestamp(),
      // Clear the staff-suspension markers so a LATER customer-side
      // suspension ("switch images") gets its normal 3-min auto-revert
      // instead of being mistaken for a staff hold.
      suspendedBy:        null,
      suspendedFromState: null,
    });
    if (activeShift) {
      await updateDoc(doc(db, "shifts", activeShift.id), {
        suspendedCount: Math.max(0, (activeShift.suspendedCount ?? 0) - 1),
      });
    }
  }

  async function rejectOrder(order) {
    setSelectedOrder(null);
    setShowRejectConfirm(false);
    await updateDoc(doc(db, "orders", order.id), { state: "rejected", rejectedAt: serverTimestamp() });
  }

  // Un-reject: back to queued with the ORIGINAL queue number — never mint a
  // new one. The number doubles as the shift's production count; a second
  // number for a pancake that was never made inflates it. A restored low
  // number sorting to the front is correct (Dan restored it deliberately);
  // the ↩ restored badge on the card explains why it's there.
  async function restoreRejected(order) {
    await updateDoc(doc(db, "orders", order.id), {
      state: "queued",
      rejectedAt: null,
      restoredFromRejection: true,
      restoredAt: serverTimestamp(),
    });
  }

  async function redoOrder(order) {
    if (!activeShift) return;
    // Assign a fresh queue number
    let queueNumber = null;
    try {
      const counterSnap = await getDocs(query(collection(db, "shifts", activeShift.id, "meta")));
      if (!counterSnap.empty) {
        const counterRef = counterSnap.docs[0].ref;
        queueNumber = await runTransaction(db, async (tx) => {
          const counterDoc = await tx.get(counterRef);
          const current = counterDoc.data().lastNumber ?? 0;
          const next = current + 1;
          tx.update(counterRef, { lastNumber: next });
          return next;
        });
      }
    } catch (e) { console.warn("Queue number assignment failed:", e); }
    // Reset order to queued state
    await updateDoc(doc(db, "orders", order.id), {
      state: "queued",
      queueNumber: queueNumber ?? order.queueNumber,
      startedAt: null,
      cookingStartedAt: null,
      flippedAt: null,
      completedAt: null,
      deliveredAt: null,
      drawingTimeMs: null,
      cookTimeMs: null,
      artistName: null,
      artistId: null,
      artistTags: null,
      autoClosedWithShift: null,
      redoneAt: serverTimestamp(),
      redoneFromOrderId: order.id,
      suspensionCount: 0,
    });
  }

  async function toggleMeditationMark(order) {
    await updateDoc(doc(db, "orders", order.id), {
      meditationMark: !order.meditationMark,
      updatedAt: serverTimestamp(),
    });
  }

  async function markDelivered(order) {
    // Delivery checkpoint: combo goods change hands HERE. Cancel aborts the
    // whole delivery so an obligation can never silently vanish.
    const addOnPrompt = addOnDeliveryPrompt(order);
    if (addOnPrompt && !window.confirm(addOnPrompt)) return;
    const aftercare = order.aftercare || "deliver";
    const isPreserve = aftercare === "preserve" || aftercare === "ship" || aftercare === "refrigerate";
    await updateDoc(doc(db, "orders", order.id), {
      state: "done",
      deliveredAt: serverTimestamp(),
      ...(addOnPrompt ? { addOns: fulfillAllAddOns(order) } : {}),
      ...(isPreserve ? { preservationStatus: "drying" } : {}),
    });
    if (activeShift) updateDoc(doc(db, "shifts", activeShift.id), { lastActivityAt: serverTimestamp() }).catch(() => {});
    const name = order.recipientName || order.subjectName || order.customerName || "";
    writeAlert("pancake_done", { name }).catch(() => {});
  }

  // Staff combo stamp — the register path: guest pays at the Square POS
  // ("Artist Combo" / "Upgrade → …" SKU), staff stamps the obligation here.
  // The money side lives entirely in Square; this only creates the goods
  // trail. Stamping never touches queue/payment fields.
  async function stampCombo(order, comboKey) {
    const combo = COMBOS[comboKey];
    if (!combo) return;
    if (!window.confirm(`Stamp ${combo.emoji} ${combo.label} on ${order.recipientName || order.subjectName || "this order"}?\n\n(Ring it on the Square POS separately — this only tracks the goods.)`)) return;
    await updateDoc(doc(db, "orders", order.id), {
      comboKey,
      // Goods-mode shift → the POS ring was the goods-only bundle (artist =
      // bare frameless kit); market shift → full framed combo.
      addOns: comboAddOnsFor(comboKey, { goods: shiftSellsGoods(activeShift) }),
      comboStampedBy: "staff",
      updatedAt: serverTimestamp(),
    });
  }

  // Tap a badge to flip one add-on between owed and handed-over (epoch-ms
  // stamp — serverTimestamp can't live inside arrays).
  async function toggleAddOnFulfilled(order, idx) {
    const next = (order.addOns || []).map((a, i) =>
      i === idx ? { ...a, fulfilledAt: a.fulfilledAt ? null : Date.now() } : a);
    await updateDoc(doc(db, "orders", order.id), { addOns: next, updatedAt: serverTimestamp() });
  }

  // Content pieces have no customer to hand off to. After the photo step, staff
  // either preserve (enter preservation pipeline) or discard (just mark done).
  async function markContentDone(order, preserve) {
    await updateDoc(doc(db, "orders", order.id), {
      state: "done",
      deliveredAt: serverTimestamp(),
      ...(preserve ? { isPreservation: true, preservationStatus: "pending", aftercare: "preserve" } : {}),
    });
    setSelectedOrder(null);
  }

  async function finishWithoutPhoto(order) {
    setSelectedOrder(null);
    setShowFinishWithoutPhotoConfirm(false);
    const aftercare = order.aftercare || "deliver";
    const isPreserve = aftercare === "preserve" || aftercare === "ship" || aftercare === "refrigerate";
    await updateDoc(doc(db, "orders", order.id), {
      state: "done", completedAt: serverTimestamp(), finishedWithoutPhoto: true,
      ...(isPreserve ? { preservationStatus: "drying" } : {}),
    });
    // Don't reset currentNumber to 0 — keep it at the completed order's number
    // so the next customer's wait estimate doesn't inflate.
  }

  if (shiftLoading) {
    return <div style={{ background: BRAND.dark, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ color: BRAND.dim }}>Loading...</p></div>;
  }

  // Two or more queues are live and this device hasn't said which it's
  // working. Never guess — a wrong guess puts an order on the wrong griddle
  // at the wrong venue. On a normal one-queue day this branch is unreachable.
  if (needsShiftChoice || showShiftPicker) {
    return (
      <ShiftPicker
        candidates={activeCandidates}
        audience="staff"
        onPick={(s) => { bindShift(s); setActiveShift(s); setShowShiftPicker(false); }}
      />
    );
  }

  // Resolve active tab — auto-detect based on shift state, or use explicit selection
  const activeTab = queueTab || (activeShift ? "live" : "studio");

  if ((!activeShift || openAnother) && activeTab === "live") {
    // Live tab without a shift — show ShiftManager to open one. Also the
    // deliberate "open a second concurrent queue" path (openAnother), which
    // passes activeShift={null} so ShiftManager renders its open form even
    // though another event is already cooking somewhere else.
    return (
      <div style={{ background: BRAND.dark, minHeight: "100vh", fontFamily: FONT.body }}>
        <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>🥞 Queue</div>
          <div style={{ flex: 1 }} />
          {openAnother
            ? <button onClick={() => setOpenAnother(false)} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.78rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}>← Back to queue</button>
            : <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.78rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}>← Menu</button>}
        </div>
        {openAnother && activeCandidates.length > 0 && (
          <div style={{ padding: "0.6rem 1rem", background: "rgba(252,179,31,0.1)", borderBottom: `1px solid ${BRAND.border}`, color: BRAND.gold, fontSize: "0.8rem" }}>
            ⚠️ {activeCandidates.length === 1 ? "Another queue is" : `${activeCandidates.length} other queues are`} already live
            {activeCandidates[0]?.eventName ? ` (${activeCandidates.map(s => s.eventName || "unnamed").join(", ")})` : ""}.
            Opening here starts a <strong>separate</strong> queue with its own numbers and its own booth code.
          </div>
        )}
        <QueueTabs active={activeTab} onSwitch={setQueueTab} studioCount={studioOrders.length} />
        <ShiftManager activeShift={null} onShiftOpen={(shift) => { setActiveShift(shift); setOpenAnother(false); }} onShiftClose={() => setActiveShift(null)} user={user} />
        <PastShiftsList shifts={pastShifts} loading={pastShiftsLoading} />
      </div>
    );
  }

  if (activeTab === "studio" && !selectedOrder) {
    // Studio tab — always visible, no shift required
    const LONG_PRESS_MS = 350;
    const MOVE_CANCEL_PX = 6;
    const TILE_GAP_PX = 8; // matches the 0.5rem gap on the tile container

    const onTilePointerDown = (e, order, index) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (studioDrag) return; // already dragging; ignore secondary pointers
      const tileEl = e.currentTarget;
      const rect = tileEl.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      const pointerId = e.pointerId;
      const slotPitch = rect.height + TILE_GAP_PX;

      const timer = setTimeout(() => {
        const d = studioDragRef.current;
        if (!d || d.canceled) return;
        try { tileEl.setPointerCapture(pointerId); } catch (_) {}
        if (navigator.vibrate) { try { navigator.vibrate(12); } catch (_) {} }
        d.dragging = true;
        setStudioDrag({
          id: order.id,
          fromIndex: index,
          toIndex: index,
          deltaY: 0,
          slotPitch,
          frozenList: studioOrders.slice(),
        });
      }, LONG_PRESS_MS);

      studioDragRef.current = {
        timer, pointerId, startX, startY, slotPitch,
        canceled: false, dragging: false, suppressClick: false,
      };
    };

    const onTilePointerMove = (e) => {
      const d = studioDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.dragging) {
        // Pre-drag — any meaningful motion cancels the long-press (treat as scroll)
        if (Math.abs(dx) > MOVE_CANCEL_PX || Math.abs(dy) > MOVE_CANCEL_PX) {
          d.canceled = true;
          clearTimeout(d.timer);
          studioDragRef.current = null;
        }
        return;
      }
      // Active drag — update position + slot
      e.preventDefault();
      setStudioDrag(s => {
        if (!s) return s;
        const offset = Math.round(dy / s.slotPitch);
        const toIndex = Math.max(0, Math.min(s.frozenList.length - 1, s.fromIndex + offset));
        return { ...s, deltaY: dy, toIndex };
      });
    };

    const onTilePointerUp = async (e) => {
      const d = studioDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      clearTimeout(d.timer);
      if (!d.dragging) {
        // Tap (or canceled long-press) — let onClick fire normally
        studioDragRef.current = null;
        return;
      }
      // Commit reorder — keep ref alive briefly so the click event that fires
      // immediately after pointerup can read suppressClick and bail. A short
      // timeout clears it if no click fires (e.g. browser swallowed it).
      d.suppressClick = true;
      const snapshot = studioDrag;
      setStudioDrag(null);
      setTimeout(() => { if (studioDragRef.current === d) studioDragRef.current = null; }, 150);
      if (!snapshot) return;
      const { fromIndex, toIndex, frozenList } = snapshot;
      if (fromIndex === toIndex) return;
      const reordered = frozenList.slice();
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      try {
        const batch = writeBatch(db);
        reordered.forEach((o, i) => {
          batch.update(doc(db, "orders", o.id), { sortIndex: (i + 1) * 1000 });
        });
        await batch.commit();
      } catch (err) {
        console.error("studio reorder failed:", err);
      }
    };

    const onTilePointerCancel = (e) => {
      const d = studioDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      clearTimeout(d.timer);
      studioDragRef.current = null;
      setStudioDrag(null);
    };

    const onTileClick = (order) => {
      // Suppress the click that follows a drag-release pointerup
      if (studioDragRef.current?.suppressClick) {
        studioDragRef.current = null;
        return;
      }
      setSelectedOrder(order);
      setQueueTab(null);
    };

    const submitNewContent = async () => {
      const name = newContentName.trim();
      const blob = newContentCroppedBlob;
      // Full-piece mode uploads the whole uncropped image; circle mode uploads
      // the square FramingTool crop. Allow submit if we have a name or an image.
      const hasFullImage = newContentFullPiece && !!newContentImageFile;
      if (!name && !blob && !hasFullImage) return;
      if (newContentSubmitting) return;
      setNewContentSubmitting(true);
      try {
        // Full-piece: normalize (HEIC-safe) + downscale the raw file BEFORE the
        // order doc exists, so a decode/convert failure aborts cleanly.
        let fullBlob = null;
        if (hasFullImage) {
          const { file: normalized, error } = await normalizePhotoForUpload(newContentImageFile);
          if (error) { alert(error); setNewContentSubmitting(false); return; }
          fullBlob = await downscaleToJpeg(normalized);
        }
        const docRef = await addDoc(collection(db, "orders"), {
          state: "queued",
          priority: "content",
          aftercare: "deliver",
          fulfillmentMode: "studio",
          source: "artist",
          subjectName: name || null,
          recipientName: "Content",
          orderType: "custom",
          queue: "standby",
          shiftId: null,
          queueNumber: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        if (fullBlob) {
          // Whole image, no crop → imageUrl (no framing fracs). The artist view
          // shows it objectFit:contain and lets Dan pan/zoom + Full griddle.
          const path = `orders/${docRef.id}/full_${Date.now()}.jpg`;
          const ref = storageRef(storage, path);
          await uploadBytes(ref, fullBlob, { contentType: "image/jpeg" });
          const url = await getDownloadURL(ref);
          await updateDoc(docRef, { imageUrl: url, updatedAt: serverTimestamp() });
        } else if (blob) {
          const path = `orders/${docRef.id}/cropped_${Date.now()}.jpg`;
          const ref = storageRef(storage, path);
          await uploadBytes(ref, blob, { contentType: "image/jpeg" });
          const url = await getDownloadURL(ref);
          await updateDoc(docRef, { croppedImageUrl: url, updatedAt: serverTimestamp() });
        }
        setNewContentName("");
        setNewContentImageFile(null);
        setNewContentCroppedBlob(null);
        setNewContentFullPiece(false);
        setShowNewContent(false);
      } catch (err) {
        console.error("submitNewContent failed:", err);
        alert(`Couldn't add content piece: ${err.message}`);
      } finally {
        setNewContentSubmitting(false);
      }
    };
    return (
      <div style={{ background: BRAND.dark, minHeight: "100vh", fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>🥞 Queue</div>
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.78rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}>← Menu</button>
        </div>
        <QueueTabs active={activeTab} onSwitch={setQueueTab} studioCount={studioOrders.length} />
        <div style={{ flex: 1, overflowY: "auto", padding: "0.75rem" }}>
          {studioOrders.length === 0 ? (
            <div style={{ color: BRAND.dim, textAlign: "center", padding: "3rem", fontSize: "0.95rem" }}>
              No studio orders yet. Commissions and content pieces will appear here.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {(studioDrag?.frozenList ?? studioOrders).map((order, i) => {
                // Content pieces use recipientName="Content" as a category label, so subjectName wins for the title
                const name = order.priority === "content"
                  ? (order.subjectName || order.recipientName || "Untitled")
                  : (order.commissionGuestName || order.recipientName || order.subjectName || "Untitled");
                const thumbSrc = order.commissionImageUrl || order.imageUrl || order.croppedImageUrl;
                const hasRef = !!thumbSrc;
                const isDrawing = order.state === "drawing" || order.state === "cooking" || order.state === "flipping";
                // Drag transforms — picked tile follows finger, others slide to make space
                let translateY = 0, scale = 1, zIndex = 0, shadow = "none";
                let transition = "transform 0.18s ease, box-shadow 0.18s ease";
                if (studioDrag) {
                  if (order.id === studioDrag.id) {
                    translateY = studioDrag.deltaY;
                    scale = 1.04;
                    zIndex = 10;
                    shadow = "0 10px 30px rgba(0,0,0,0.55)";
                    transition = "none";
                  } else if (studioDrag.fromIndex < studioDrag.toIndex && i > studioDrag.fromIndex && i <= studioDrag.toIndex) {
                    translateY = -studioDrag.slotPitch;
                  } else if (studioDrag.fromIndex > studioDrag.toIndex && i >= studioDrag.toIndex && i < studioDrag.fromIndex) {
                    translateY = studioDrag.slotPitch;
                  }
                }
                return (
                  <button
                    key={order.id}
                    onClick={() => onTileClick(order)}
                    onPointerDown={(e) => onTilePointerDown(e, order, i)}
                    onPointerMove={onTilePointerMove}
                    onPointerUp={onTilePointerUp}
                    onPointerCancel={onTilePointerCancel}
                    style={{
                      background: BRAND.card, border: `1px solid ${isDrawing ? BRAND.gold : BRAND.border}`, borderRadius: "12px",
                      padding: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "center",
                      cursor: "pointer", textAlign: "left", width: "100%",
                      transform: `translateY(${translateY}px) scale(${scale})`,
                      transition,
                      boxShadow: shadow,
                      zIndex,
                      position: "relative",
                      touchAction: "pan-y",
                      userSelect: "none",
                  }}>
                    <div style={{
                      width: "56px", height: "56px", borderRadius: "10px", flexShrink: 0,
                      background: hasRef ? "#3a4458" : "rgba(255,255,255,0.05)",
                      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                    }}>
                      {hasRef ? (
                        <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontSize: "1.5rem", opacity: 0.3 }}>📸</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontWeight: 700, fontSize: "0.95rem" }}>{name}</div>
                      {order.commissionNotes && (
                        <div style={{ color: BRAND.muted, fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.commissionNotes}</div>
                      )}
                      <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.2rem", alignItems: "center" }}>
                        <span style={{ color: BRAND.dim, fontSize: "0.68rem" }}>
                          {order.priority === "commission" ? "🎨 Commission" : order.priority === "content" ? "💡 Content" : order.priority || "Studio"}
                        </span>
                        {order.source === "shop" && order.totalPaidCents > 0 && (
                          <span style={{ color: BRAND.green, fontSize: "0.68rem" }}>${(order.totalPaidCents / 100).toFixed(0)} paid</span>
                        )}
                      </div>
                    </div>
                    {!order.commissionSubmittedAt && order.source === "shop" && (
                      <span style={{ color: BRAND.orange, fontSize: "0.68rem", fontWeight: 700, flexShrink: 0 }}>Awaiting photo</span>
                    )}
                    {isDrawing && (
                      <span style={{ color: BRAND.gold, fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>In progress</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* New content piece — text-only fast path; tap 📷 for image+crop */}
          {showNewContent ? (
            <div style={{ display: "flex", gap: "0.5rem", padding: "0.5rem 0" }}>
              <input
                value={newContentName}
                onChange={e => setNewContentName(e.target.value)}
                placeholder="Subject — e.g. Guy Fieri, Bob Ross…"
                autoFocus
                style={{ flex: 1, background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.9rem", padding: "0.5rem 0.75rem", outline: "none" }}
                onKeyDown={e => {
                  if (e.key === "Enter" && newContentName.trim()) submitNewContent();
                }}
              />
              <button
                onClick={() => newContentFileInputRef.current?.click()}
                title="Add reference image"
                style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem 0.65rem", color: BRAND.cream, fontSize: "1rem", cursor: "pointer", flexShrink: 0 }}
              >
                📷
              </button>
              <button
                onClick={() => { if (newContentName.trim()) submitNewContent(); }}
                disabled={!newContentName.trim()}
                style={{ background: newContentName.trim() ? BRAND.gold : BRAND.border, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.5rem 0.75rem", fontWeight: 800, fontSize: "0.85rem", cursor: newContentName.trim() ? "pointer" : "default", flexShrink: 0, opacity: newContentName.trim() ? 1 : 0.5 }}
              >
                Add
              </button>
              <button
                onClick={() => { setShowNewContent(false); setNewContentName(""); }}
                style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem", color: BRAND.dim, fontSize: "0.85rem", cursor: "pointer" }}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewContent(true)}
              style={{ width: "100%", background: "transparent", border: `1px dashed ${BRAND.border}`, borderRadius: "10px", padding: "0.7rem", color: BRAND.muted, fontSize: "0.85rem", cursor: "pointer", marginTop: "0.25rem" }}
            >
              + New content piece
            </button>
          )}
          <input
            ref={newContentFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) {
                setNewContentImageFile(f);
                setNewContentCroppedBlob(null);
                setNewContentFullPiece(false);
                setShowNewContent(true);
              }
              e.target.value = "";
            }}
          />
        </div>
        {activeShift && (
          <div style={{ padding: "0.5rem 1rem", background: BRAND.panel, borderTop: `1px solid ${BRAND.border}` }}>
            <ShiftManager activeShift={activeShift} onShiftOpen={(shift) => setActiveShift(shift)} onShiftClose={() => setActiveShift(null)} user={user} />
          </div>
        )}
        {newContentImageFile && (() => {
          // Circle-crop mode is ready once FramingTool has produced a blob;
          // full-piece mode is ready as soon as an image is picked (submit
          // normalizes + downscales the raw file).
          const ready = newContentFullPiece ? !!newContentImageFile : !!newContentCroppedBlob;
          const canSubmit = ready && !newContentSubmitting;
          return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem", overflowY: "auto" }}>
            <div style={{ color: BRAND.gold, fontSize: "1.1rem", fontWeight: 800, fontFamily: FONT.display, marginBottom: "0.6rem" }}>
              {newContentFullPiece ? "Full piece" : "Frame reference"}
            </div>
            {/* Mode toggle — circle crop (default) vs. whole image for wide art */}
            <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.85rem", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "999px", padding: "0.25rem" }}>
              {[
                { on: false, label: "◯ Circle crop" },
                { on: true,  label: "▭ Full piece" },
              ].map(m => {
                const active = newContentFullPiece === m.on;
                return (
                  <button
                    key={m.label}
                    onClick={() => setNewContentFullPiece(m.on)}
                    style={{ background: active ? BRAND.gold : "transparent", color: active ? BRAND.dark : BRAND.cream, border: "none", borderRadius: "999px", padding: "0.4rem 0.9rem", fontWeight: 800, fontSize: "0.82rem", cursor: "pointer" }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.82rem", textAlign: "center", marginBottom: "0.85rem", maxWidth: "360px", lineHeight: 1.4 }}>
              {newContentFullPiece
                ? "The whole image goes in uncropped. You'll arrange it on the full griddle in the artist view (pan, zoom, ◫ Full)."
                : "Pinch / drag to crop out anything you don't want to see while drawing. You can still zoom and pan in the artist view."}
            </div>
            {newContentFullPiece ? (
              <img
                src={newContentPreviewUrl}
                alt="Reference preview"
                style={{ maxWidth: "min(360px, 100%)", maxHeight: "48vh", objectFit: "contain", borderRadius: "8px", boxShadow: "0 2px 16px rgba(0,0,0,0.6)", background: "#0a1230" }}
              />
            ) : (
              <FramingTool
                imageFile={newContentImageFile}
                onCropped={blob => setNewContentCroppedBlob(blob)}
              />
            )}
            <input
              value={newContentName}
              onChange={e => setNewContentName(e.target.value)}
              placeholder="Subject (optional)"
              style={{ marginTop: "1rem", width: "100%", maxWidth: "360px", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.95rem", padding: "0.6rem 0.8rem", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: "0.6rem", marginTop: "0.85rem", width: "100%", maxWidth: "360px" }}>
              <button
                onClick={() => { setNewContentImageFile(null); setNewContentCroppedBlob(null); setNewContentFullPiece(false); }}
                style={{ flex: 1, background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.65rem", color: BRAND.cream, fontSize: "0.9rem", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={submitNewContent}
                disabled={!canSubmit}
                style={{ flex: 2, background: canSubmit ? BRAND.gold : BRAND.border, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.65rem", fontWeight: 800, fontSize: "0.95rem", cursor: canSubmit ? "pointer" : "default", opacity: canSubmit ? 1 : 0.5 }}
              >
                {newContentSubmitting ? "Adding…" : "Add to studio"}
              </button>
            </div>
          </div>
          );
        })()}
      </div>
    );
  }

  if (selectedOrder) {
    const order        = selectedOrder;
    const isQueued    = order.state === "queued";
    const isDrawing   = order.state === "drawing";
    const isCooking   = order.state === "cooking";
    const isFlipping  = order.state === "flipping";
    const isSuspended = order.state === "suspended";
    const cookLocked    = order.cookTimeMs != null;
    const cookStartMs   = (order.cookingStartedAt || order.flippedAt)?.toMillis?.() ?? now;
    const cookDisplayMs = cookLocked ? order.cookTimeMs : now - cookStartMs;
    const mainOrders   = orders.filter(o => o.queue !== "standby");
    const currentIndex = mainOrders.findIndex(o => o.id === order.id);
    const prevOrder    = currentIndex > 0 ? mainOrders[currentIndex - 1] : null;
    const nextOrder    = currentIndex >= 0 && currentIndex < mainOrders.length - 1 ? mainOrders[currentIndex + 1] : null;
    const atDefault    = xform.scale <= 1.05; // hide pills only when zoomed in, not on minor pan drift

    return (
      <div ref={outerContainerRef} style={{ background: "#000", height: "100dvh", display: "flex", flexDirection: "column", fontFamily: FONT.body, userSelect: "none", touchAction: "none", position: "relative", overflow: "hidden" }}>

        {/* Top chrome — HUD bar + any submenus that hang off it. Wrapping
            the two in a `position: relative` host means submenus can sit
            at `top: 100%` (right below the HUD) as absolute overlays rather
            than in-flow siblings. That way opening Grid/rotation panels no
            longer resizes the image area — only hiding the whole chrome
            via "Hide UI" does, which is intentional. */}
        <div style={{ position: "relative", flexShrink: 0, zIndex: 11 }}>
        {/* The HUD must never push a control off the right edge: on a 375px
            iPhone this row measured 444px, which put ⇔ Mirrored AND ⊡ Hide UI
            out of reach — i.e. the artist could neither mirror a reference nor
            clear the chrome to draw, on the device most likely to need both.
            The identity block shrinks first (while drawing you know whose
            pancake it is), and the row wraps only if that isn't enough, so
            nothing is ever unreachable at any width. */}
        {showUI && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: isLandscape ? "0.35rem 1rem" : "0.75rem 1rem", background: "#0a1230", borderBottom: `1px solid ${BRAND.border}`, gap: "0.5rem", rowGap: "0.4rem", flexWrap: "wrap" }}>
            <button onClick={() => { setSelectedOrder(null); setShowRejectConfirm(false); setShowGridOptions(false); }} style={{ ...styles.hudButton, flexShrink: 0 }}>← Back</button>
            <div style={{ textAlign: "center", flex: "1 1 6rem", minWidth: 0, overflow: "hidden" }}>
              <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {order.queueNumber ? `#${order.queueNumber} · ` : ""}{order.recipientName || order.subjectName}
              </div>
              {order.subjectName && order.recipientName && (
                <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>"{order.subjectName}"</div>
              )}
              <div style={{ color: STATE_COLORS[order.state], fontSize: "0.75rem" }}>{STATE_LABELS[order.state]}</div>
              {order.artistName && order.state !== "queued" && (
                <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>🎨 {order.artistName}</div>
              )}
              {order.comboKey && COMBOS[order.comboKey] && (
                <div style={{ color: BRAND.gold, fontSize: "0.68rem", fontWeight: "bold" }}>
                  {COMBOS[order.comboKey].emoji} {COMBOS[order.comboKey].label} — {(order.addOns || []).map(addOnLabel).join(" + ")}
                </div>
              )}
            </div>
            {/* Combo stamp — guest upgraded at the booth (rang on POS): one tap
                creates the goods obligation. Customs only; hidden once stamped. */}
            {!order._isReference && !order.comboKey && order.orderType !== "flash" && COMBO_ORDER.map((key) => (
              <button key={key} onClick={() => stampCombo(order, key)} style={{ ...styles.hudButton, color: BRAND.muted }} title={`Stamp ${COMBOS[key].label} (rang on POS)`}>
                {COMBOS[key].emoji}
              </button>
            ))}
            {!order._isReference && (
              <button onClick={() => toggleMeditationMark(order)} style={{ ...styles.hudButton, background: order.meditationMark ? "rgba(252,179,31,0.18)" : "transparent", borderColor: order.meditationMark ? BRAND.gold : BRAND.border, color: order.meditationMark ? BRAND.gold : BRAND.muted }} title="Mark for meditation">
                ✨ {order.meditationMark ? "Marked" : "Mark"}
              </button>
            )}
            <button onClick={() => setShowGridOptions(s => !s)} style={{ ...styles.hudButton, background: grid.show ? "rgba(0,255,255,0.12)" : "transparent", borderColor: grid.show ? "#00ffff" : BRAND.border, color: grid.show ? "#00ffff" : BRAND.muted }}>⊞ Grid</button>
            <button onClick={() => setMirrored(m => !m)} style={{ ...styles.hudButton, background: mirrored ? "rgba(252,179,31,0.12)" : "transparent", borderColor: mirrored ? BRAND.gold : BRAND.border, color: mirrored ? BRAND.gold : BRAND.muted }}>
              {mirrored ? "⇔ Mirrored" : "⇔ Normal"}
            </button>
            {order.tokenId && <button onClick={() => setReissueOrder(order)} style={{ ...styles.hudButton, color: BRAND.muted }} title="Re-issue ticket">🎟</button>}
            <button onClick={() => setShowUI(false)} style={{ ...styles.hudButton, flexShrink: 0 }} title="Hide UI — maximise the drawing area">⊡</button>
          </div>
        )}

        {/* Grid + rotation submenus — absolutely positioned overlay so the
            image area below doesn't resize when they open. `top: 100%` drops
            them right under the HUD; when the HUD is hidden the outer host
            has zero height so the overlay hugs the top of the screen. */}
        {showUI && showGridOptions && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 11, boxShadow: "0 6px 18px rgba(0,0,0,0.35)" }}>
          <div style={{ background: "#142548", borderBottom: `1px solid ${BRAND.border}`, padding: "0.75rem 1rem", display: "flex", gap: "1.25rem", alignItems: "center", flexWrap: "wrap" }}>
            <label style={styles.gridLabel}><input type="checkbox" checked={grid.show} onChange={e => setGrid(g => ({ ...g, show: e.target.checked }))} style={{ marginRight: "0.4rem" }} />Show grid</label>
            <label style={styles.gridLabel}><input type="checkbox" checked={grid.showCircle} onChange={e => setGrid(g => ({ ...g, showCircle: e.target.checked }))} style={{ marginRight: "0.4rem" }} />Show circle</label>
            <label style={styles.gridLabel}>
              Opacity
              <input type="range" min={0.05} max={1} step={0.05} value={grid.opacity} onChange={e => setGrid(g => ({ ...g, opacity: parseFloat(e.target.value) }))} style={{ marginLeft: "0.5rem", width: "90px" }} />
              <span style={{ marginLeft: "0.35rem", minWidth: "2.5ch" }}>{Math.round(grid.opacity * 100)}%</span>
            </label>
            <label style={styles.gridLabel}>Color<input type="color" value={grid.color} onChange={e => setGrid(g => ({ ...g, color: e.target.value }))} style={{ marginLeft: "0.5rem", width: "36px", height: "24px", cursor: "pointer", border: "none", background: "none" }} /></label>
            <button onClick={() => setGrid(g => ({ ...g, fullGriddle: !g.fullGriddle }))}
              style={{ ...styles.hudButton, fontSize: "0.7rem",
                background: grid.fullGriddle ? "rgba(0,255,255,0.18)" : "transparent",
                borderColor: grid.fullGriddle ? "#00ffff" : BRAND.border,
                color: grid.fullGriddle ? "#00ffff" : BRAND.dim,
              }}>
              {grid.fullGriddle ? "◫ Full" : "◯ Circle"}
            </button>
            <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
              <span style={{ color: BRAND.muted, fontSize: "0.68rem", marginRight: "0.15rem" }}>Side</span>
              {["left", "right"].map(s => (
                <button key={s} onClick={() => setGrid(g => ({ ...g, side: g.side === s ? null : s }))}
                  style={{ ...styles.hudButton, fontSize: "0.7rem", padding: "0.25rem 0.5rem", minWidth: "28px",
                    background: grid.side === s ? "rgba(0,255,255,0.18)" : "transparent",
                    borderColor: grid.side === s ? "#00ffff" : BRAND.border,
                    color: grid.side === s ? "#00ffff" : BRAND.dim,
                  }}>
                  {s === "left" ? "L" : "R"}
                </button>
              ))}
            </div>
            <button onClick={() => setGridLocked(l => !l)} style={{ ...styles.hudButton, fontSize: "0.7rem", background: gridLocked ? "rgba(0,255,255,0.12)" : "rgba(252,179,31,0.15)", borderColor: gridLocked ? "#00ffff" : BRAND.gold, color: gridLocked ? "#00ffff" : BRAND.gold }}>
              {gridLocked ? "🔒 Paired" : "🔓 Free"}
            </button>
            <button onClick={() => {
              setGrid(DEFAULT_GRID);
              setGridLocked(true);
              const reset     = { scale: 1, tx: 0, ty: 0, rotation: 0 };
              const gridReset = { scale: 1, tx: 0, ty: 0 };
              xformRef.current = reset;
              gridXformRef.current = gridReset;
              setXform(reset);
              if (gestureWrapperRef.current) gestureWrapperRef.current.style.transform = "translate(0px, 0px) rotate(0deg) scale(1)";
              if (gridOverlayRef.current) gridOverlayRef.current.style.transform = "translate(0px, 0px) scale(1)";
            }} style={{ ...styles.hudButton, fontSize: "0.7rem" }} title="Reset grid + image position, zoom, and rotation">↻ Reset view</button>
          </div>
          {/* Rotation controls — second row of the same overlay. Button-based
              ±90° / ±15° increments are the primary path; two-finger twist
              (opt-in) is the secondary fine-tune. */}
          <div style={{ background: "#0f1c3a", borderBottom: `1px solid ${BRAND.border}`, padding: "0.65rem 1rem", display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: BRAND.muted, fontSize: "0.72rem", letterSpacing: "0.05em", textTransform: "uppercase" }}>↻ Rotate ref</span>
            {[-90, -15, +15, +90].map(step => (
              <button
                key={step}
                onClick={() => {
                  const prev = xformRef.current.rotation || 0;
                  let next = prev + step;
                  next = ((next + 180) % 360 + 360) % 360 - 180;
                  const updated = { ...xformRef.current, rotation: next };
                  xformRef.current = updated;
                  setXform(updated);
                  if (gestureWrapperRef.current)
                    gestureWrapperRef.current.style.transform = `translate(${updated.tx}px, ${updated.ty}px) rotate(${next}deg) scale(${updated.scale})`;
                }}
                style={{ ...styles.hudButton, fontSize: "0.72rem", minWidth: "3rem" }}
                title={`Rotate ${step > 0 ? "+" : ""}${step}°`}
              >
                {step > 0 ? `+${step}°` : `${step}°`}
              </button>
            ))}
            <span style={{ color: Math.round(xform.rotation || 0) !== 0 ? BRAND.gold : BRAND.dim, fontSize: "0.78rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: "3.5ch", textAlign: "center" }}>
              {Math.round(xform.rotation || 0)}°
            </span>
            <label style={{ ...styles.gridLabel, marginLeft: "0.75rem", cursor: "pointer" }} title="Off by default so routine zooming can't tilt the reference. Turn on when you need to rotate a sideways upload; snaps to 15° on release.">
              <input
                type="checkbox"
                checked={twistRotate}
                onChange={e => setTwistRotate(e.target.checked)}
                style={{ marginRight: "0.4rem" }}
              />
              Twist to rotate
            </label>
          </div>
          </div>
        )}
        </div>

        {/* #3a4458 (not pure black) so black line-art limbs on flash PNGs (Jacques, Sugar) stay visible */}
        <div ref={touchContainerRef} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", background: "#3a4458" }}>
          {order.state === "done" && order.photo_url ? (
            /* Done — show final pancake photo */
            <div ref={gestureWrapperRef} style={{ transform: `translate(${xform.tx}px, ${xform.ty}px) rotate(${xform.rotation || 0}deg) scale(${xform.scale})`, transformOrigin: "center center", willChange: "transform", aspectRatio: "1 / 1", overflow: "hidden", ...(isLandscape ? { height: "100%", maxWidth: "100%" } : { width: "100%", maxHeight: "100%" }) }}>
              <div ref={mirrorWrapperRef} style={{ width: "100%", height: imgSize ? `${imgSize.size}px` : "100%", lineHeight: 0 }}>
                <img ref={imgRef} src={order.photo_url} alt="Final pancake" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
              </div>
            </div>
          ) : (order.imageUrl || order.croppedImageUrl || order.commissionImageUrl) ? (
            /* Custom order or commission — image with optional color swatches pinned to right edge */
            <>
              <div ref={gestureWrapperRef} style={{ transform: `translate(${xform.tx}px, ${xform.ty}px) rotate(${xform.rotation || 0}deg) scale(${xform.scale})`, transformOrigin: "center center", willChange: "transform", aspectRatio: "1 / 1", overflow: "hidden", ...(isLandscape ? { height: "100%", maxWidth: "100%" } : { width: "100%", maxHeight: "100%" }) }}>
                {/* Mirror wrapper — kept separate so scaleX(-1) doesn't interfere with pinch math */}
                <div ref={mirrorWrapperRef} style={{ transform: mirrored ? "scaleX(-1)" : "none", transition: "transform 0.3s", position: "relative", lineHeight: 0, width: "100%", height: imgSize ? `${imgSize.size}px` : "100%" }}>
                  {order.framingDrawnWidthFrac != null && imgSize ? (
                    // Full image path — imageUrl positioned using normalized framing fracs so it
                    // matches exactly what the customer saw in the FramingTool on their device.
                    <img ref={imgRef} src={order.imageUrl || order.commissionImageUrl} alt="Reference" style={{
                      position: "absolute",
                      width:    order.framingDrawnWidthFrac  * imgSize.size,
                      height:   order.framingDrawnHeightFrac * imgSize.size,
                      left:     imgSize.size / 2 + order.framingOffsetXFrac * imgSize.size - (order.framingDrawnWidthFrac  * imgSize.size) / 2,
                      top:      imgSize.size / 2 + order.framingOffsetYFrac * imgSize.size - (order.framingDrawnHeightFrac * imgSize.size) / 2,
                    }} />
                  ) : (
                    // Fallback for orders without framing params — croppedImageUrl
                    // or a full-piece content imageUrl, CSS-sized objectFit:contain
                    // so a wide uncropped image shows whole (Dan pans/zooms + Full griddle).
                    <img ref={imgRef} src={order.croppedImageUrl || order.imageUrl || order.commissionImageUrl} alt="Reference" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                  )}
                  {/* Grid always renders outside the gesture wrapper — see touchContainerRef below */}
                  {/* Color isolation overlay — shown when an extracted color is selected */}
                  {isolationOverlay && imgSize && (
                    <img src={isolationOverlay} alt="" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none", zIndex: 2 }} />
                  )}
                </div>
              </div>

              {/* A freestyle's reference is a picture the ARTIST went and
                  found, not something the guest submitted — so the guest's
                  actual words still matter and must not vanish behind it.
                  Only rendered for freestyles: a custom's image IS the brief. */}
              {(order.orderType === "freestyle_flash" || order.isFreestyle) && (
                <div style={{
                  position: "absolute", left: "12px", bottom: "12px", zIndex: 6,
                  maxWidth: "min(70%, 420px)", display: "flex", flexDirection: "column",
                  gap: "6px", alignItems: "flex-start", pointerEvents: "none",
                }}>
                  {(order.freestyleDescription || order.notes) && (
                    <div style={{
                      background: "rgba(13,24,51,0.82)", border: `1px solid ${BRAND.border}`,
                      borderRadius: "10px", padding: "6px 10px",
                      color: "white", fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.25,
                    }}>
                      ⚡ {order.freestyleDescription || order.notes}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "6px", pointerEvents: "auto" }}>
                    <button
                      onClick={() => requestAttachImage(order)}
                      style={{ padding: "4px 9px", background: "rgba(13,24,51,0.82)", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.sky, fontSize: "0.72rem", fontWeight: 600, cursor: "pointer" }}
                    >📎 Replace</button>
                    <button
                      onClick={() => detachReferenceImage(order)}
                      style={{ padding: "4px 9px", background: "rgba(13,24,51,0.82)", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.dim, fontSize: "0.72rem", fontWeight: 600, cursor: "pointer" }}
                    >✕ Remove</button>
                  </div>
                </div>
              )}
              {order.favoriteColors?.length > 0 && (() => {
                const colorObjs = order.favoriteColors
                  .map(c => typeof c === "string" ? COLOR_OPTIONS.find(o => o.id === c) : c)
                  .filter(Boolean);
                return colorObjs.length > 0 && (
                  <div style={{ position: "absolute", right: "12px", top: "12px", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", zIndex: 5, pointerEvents: "none" }}>
                    {colorObjs.map(color => (
                      <div key={color.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                        <div style={{
                          width: "68px", height: "68px", borderRadius: "50%",
                          background: color.hex,
                          boxShadow: "0 0 0 3px rgba(0,0,0,0.85), 0 3px 14px rgba(0,0,0,0.6)",
                          ...(color.id === "white" || color.id === "black" ? { outline: `2px solid rgba(255,255,255,0.35)` } : {}),
                        }} />
                        <span style={{ color: "white", fontSize: "0.6rem", fontWeight: "bold", textShadow: "0 1px 3px rgba(0,0,0,0.9)", letterSpacing: "0.04em" }}>{color.label}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          ) : order.orderType === "flash" && order.designId ? (
            /* Flash design — character PNG, mirrored, no grid. Color swatches pinned to right edge. */
            <>
              <div ref={gestureWrapperRef} style={{ transform: `translate(${xform.tx}px, ${xform.ty}px) rotate(${xform.rotation || 0}deg) scale(${xform.scale})`, transformOrigin: "center center", willChange: "transform", aspectRatio: "1 / 1", overflow: "hidden", ...(isLandscape ? { height: "100%", maxWidth: "100%" } : { width: "100%", maxHeight: "100%" }) }}>
                <div ref={mirrorWrapperRef} style={{ transform: mirrored ? "scaleX(-1)" : "none", transition: "transform 0.3s", position: "relative", lineHeight: 0, width: "100%", height: imgSize ? `${imgSize.size}px` : "100%" }}>
                  <img ref={imgRef} src={designMap[order.designId]?.imageUrl || `/flash-designs/${order.designId}.png`} alt={order.designLabel} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                </div>
              </div>
              {order.favoriteColors?.length > 0 && (() => {
                const colorObjs = order.favoriteColors
                  .map(c => typeof c === "string" ? COLOR_OPTIONS.find(o => o.id === c) : c)
                  .filter(Boolean);
                return colorObjs.length > 0 && (
                  <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", zIndex: 5, pointerEvents: "none" }}>
                    {colorObjs.map(color => (
                      <div key={color.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                        <div style={{
                          width: "68px",
                          height: "68px",
                          borderRadius: "50%",
                          background: color.hex,
                          boxShadow: "0 0 0 3px rgba(0,0,0,0.85), 0 3px 14px rgba(0,0,0,0.6)",
                          ...(color.id === "white" || color.id === "black" ? { outline: `2px solid rgba(255,255,255,0.35)` } : {}),
                        }} />
                        <span style={{ color: "white", fontSize: "0.6rem", fontWeight: "bold", textShadow: "0 1px 3px rgba(0,0,0,0.9)", letterSpacing: "0.04em" }}>{color.label}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          ) : (order.orderType === "freestyle_flash" || order.isFreestyle) ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", padding: "2rem", textAlign: "center", gap: "1rem" }}>
                <div style={{ fontSize: "2.5rem" }}>⚡</div>
                <div style={{ color: "white", fontSize: "1.75rem", fontWeight: "bold", lineHeight: 1.4, maxWidth: "320px" }}>
                  {order.freestyleDescription || order.notes}
                </div>
                {/* Go find a picture of what they asked for — only worth it
                    when the griddle is quiet, so it's an offer, never a step.
                    Attaching sets `imageUrl`, which flips this card to the
                    reference view above with no other plumbing. */}
                <button
                  onClick={() => requestAttachImage(order)}
                  style={{
                    marginTop: "0.4rem", padding: "0.6rem 1.1rem",
                    background: "rgba(255,255,255,0.08)",
                    border: `1px solid ${BRAND.border}`, borderRadius: "10px",
                    color: BRAND.sky, fontSize: "0.9rem", fontWeight: 600,
                    cursor: "pointer",
                  }}
                >📎 Add reference image</button>
              </div>
              {order.favoriteColors?.length > 0 && (() => {
                const colorObjs = order.favoriteColors
                  .map(c => typeof c === "string" ? COLOR_OPTIONS.find(o => o.id === c) : c)
                  .filter(Boolean);
                return colorObjs.length > 0 && (
                  <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", zIndex: 5, pointerEvents: "none" }}>
                    {colorObjs.map(color => (
                      <div key={color.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                        <div style={{
                          width: "68px", height: "68px", borderRadius: "50%",
                          background: color.hex,
                          boxShadow: "0 0 0 3px rgba(0,0,0,0.85), 0 3px 14px rgba(0,0,0,0.6)",
                          ...(color.id === "white" || color.id === "black" ? { outline: `2px solid rgba(255,255,255,0.35)` } : {}),
                        }} />
                        <span style={{ color: "white", fontSize: "0.6rem", fontWeight: "bold", textShadow: "0 1px 3px rgba(0,0,0,0.9)", letterSpacing: "0.04em" }}>{color.label}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          ) : (
            <div style={{ color: BRAND.dim, fontSize: "1rem" }}>No image</div>
          )}

          {(isDrawing || isCooking || isFlipping) && (
            <div style={{ position: "absolute", top: "0.65rem", left: "0.75rem", zIndex: 5, background: "rgba(0,0,0,0.65)", borderRadius: "10px", padding: "0.4rem 0.75rem", fontFamily: "monospace", display: "flex", flexDirection: "column", gap: "0.15rem", backdropFilter: "blur(4px)" }}>
              {isDrawing && (
                <div style={{ color: BRAND.blue, fontSize: "0.95rem", fontWeight: "bold" }}>
                  ✏️ {fmtStopwatch(now - (order.startedAt?.toMillis?.() ?? now))}
                </div>
              )}
              {(isCooking || isFlipping) && (
                <>
                  <div style={{ color: BRAND.dim, fontSize: "0.75rem" }}>✏️ {fmtMs(order.drawingTimeMs)} <span style={{ color: "rgba(255,255,255,0.2)" }}>locked</span></div>
                  <div style={{ color: cookLocked ? BRAND.dim : BRAND.orange, fontSize: "0.95rem", fontWeight: "bold" }}>🔥 {fmtStopwatch(cookDisplayMs)}{cookLocked && <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: "normal" }}> locked</span>}</div>
                  <div style={{ color: BRAND.muted, fontSize: "0.8rem", borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.2rem", marginTop: "0.05rem" }}>
                    ⏱ {fmtStopwatch((order.drawingTimeMs ?? 0) + cookDisplayMs)}
                  </div>
                </>
              )}
              {(() => {
                const isCustom = order.orderType === "custom";
                const bestDraw  = isCustom ? activeShift?.shiftBestCustomDrawMs  : activeShift?.shiftBestFlashDrawMs;
                const bestTotal = isCustom ? activeShift?.shiftBestCustomTotalMs : activeShift?.shiftBestFlashTotalMs;
                if (!bestDraw) return null;
                return (
                  <div style={{ color: BRAND.muted, fontSize: "0.65rem", borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.15rem", marginTop: "0.05rem" }}>
                    best ✏️ {fmtMs(bestDraw)}{bestTotal ? ` ⏱ ${fmtMs(bestTotal)}` : ""}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Grid — own transform, paired/free with image via gesture handlers */}
          {grid.show && imgSize && (() => {
            const cell = imgSize.size / 12;
            if (grid.fullGriddle) {
              const fw = 26 * cell;
              const fh = 24 * cell;
              // Position so the active circle center aligns with view center.
              // Left circle center: (6.5*cell, 11*cell), Right: (19.5*cell, 11*cell)
              // No side selected: center the whole griddle.
              const anchorX = grid.side === 'right' ? 19.5 * cell : grid.side === 'left' ? 6.5 * cell : fw / 2;
              const anchorY = 12 * cell;
              return (
                <div ref={gridOverlayRef} style={{
                  position: "absolute", top: "50%", left: "50%",
                  width: fw, height: fh,
                  marginLeft: -anchorX, marginTop: -anchorY,
                  transform: "translate(0px, 0px) scale(1)",
                  transformOrigin: `${anchorX}px ${anchorY}px`,
                  pointerEvents: "none", zIndex: 4,
                }}>
                  <GridOverlay size={imgSize.size} color={grid.color} opacity={grid.opacity} showCircle={grid.showCircle} showLabels={grid.showLabels} side={grid.side} fullGriddle />
                </div>
              );
            }
            return (
              <div ref={gridOverlayRef} style={{
                position: "absolute", top: "50%", left: "50%",
                width: imgSize.size, height: imgSize.size,
                marginLeft: -imgSize.size / 2, marginTop: -imgSize.size / 2,
                transform: "translate(0px, 0px) scale(1)",
                transformOrigin: "center center",
                pointerEvents: "none", zIndex: 4,
              }}>
                <GridOverlay size={imgSize.size} color={grid.color} opacity={grid.opacity} showCircle={grid.showCircle} showLabels={grid.showLabels} side={grid.side} />
              </div>
            );
          })()}

          {/* Extracted color swatches — bottom-left */}
          {extractedColors.length > 0 && showUI && (
            <div style={{ position: "absolute", bottom: "12px", left: "12px", display: "flex", gap: "8px", alignItems: "flex-end", zIndex: 6, flexWrap: "wrap", maxWidth: "280px" }}>
              {extractedColors.slice(0, 7).map((c, i) => (
                <button
                  key={i}
                  onClick={() => handleColorTap(i)}
                  style={{
                    width: "42px", height: "42px", borderRadius: "50%",
                    background: c.hex, border: isolatedColorIdx === i ? "3px solid white" : "2px solid rgba(0,0,0,0.7)",
                    boxShadow: isolatedColorIdx === i ? "0 0 12px rgba(255,255,255,0.5), 0 2px 8px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.5)",
                    cursor: "pointer", padding: 0, flexShrink: 0,
                    transform: isolatedColorIdx === i ? "scale(1.2)" : "scale(1)",
                    transition: "transform 0.2s, border 0.2s, box-shadow 0.2s",
                  }}
                  title={`${c.hex} — ${Math.round(c.pct * 100)}%`}
                />
              ))}
              {isolatedColorIdx !== null && (
                <button
                  onClick={() => { setIsolatedColorIdx(null); setIsolationOverlay(null); }}
                  style={{
                    background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: "8px", padding: "6px 10px", color: "rgba(255,255,255,0.7)",
                    fontSize: "0.7rem", cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >✕ Clear</button>
              )}
            </div>
          )}
        </div>

        {/* Edge nav buttons and UI restore — rendered at outer-container level so they
            always stack above the "START THIS PANCAKE?" overlay (zIndex: 10) */}
        {prevOrder && atDefault && (
          <button
            onClick={() => { setSelectedOrder(prevOrder); setShowRejectConfirm(false); setShowGridOptions(false); }}
            style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 10, background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px", padding: "8px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", maxWidth: "72px", cursor: "pointer" }}
          >
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem", lineHeight: 1 }}>‹</div>
            {prevOrder.queueNumber && <div style={{ color: "white", fontSize: "0.7rem", fontWeight: "bold" }}>#{prevOrder.queueNumber}</div>}
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.6rem", textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>{prevOrder.recipientName || prevOrder.subjectName}</div>
          </button>
        )}

        {nextOrder && atDefault && (
          <button
            onClick={() => { setSelectedOrder(nextOrder); setShowRejectConfirm(false); setShowGridOptions(false); }}
            style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 10, background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px", padding: "8px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", maxWidth: "72px", cursor: "pointer" }}
          >
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem", lineHeight: 1 }}>›</div>
            {nextOrder.queueNumber && <div style={{ color: "white", fontSize: "0.7rem", fontWeight: "bold" }}>#{nextOrder.queueNumber}</div>}
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.6rem", textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>{nextOrder.recipientName || nextOrder.subjectName}</div>
          </button>
        )}

        {!showUI && (
          <button
            onClick={() => setShowUI(true)}
            style={{ position: "absolute", top: "0.75rem", right: "0.75rem", zIndex: 10, background: "rgba(0,0,0,0.55)", border: `1px solid ${BRAND.dim}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.8rem", padding: "0.4rem 0.75rem", cursor: "pointer" }}
          >
            ☰
          </button>
        )}

        {showUI && order.notes && order.orderType !== "freestyle_flash" && (
          <div style={{ padding: isLandscape ? "0.4rem 1rem" : "0.75rem 1rem", background: "rgba(252,179,31,0.12)", borderTop: "2px solid rgba(252,179,31,0.5)", color: BRAND.gold, fontSize: isLandscape ? "1rem" : "1.15rem", fontWeight: "600", lineHeight: 1.4, flexShrink: 0 }}>📝 {order.notes}</div>
        )}

        {showUI && order.state === "done" && order.ai_description && (
          <div style={{ padding: isLandscape ? "0.25rem 1rem" : "0.5rem 1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.divider}`, color: BRAND.muted, fontSize: "0.78rem", fontStyle: "italic", lineHeight: 1.5, flexShrink: 0 }}>
            "{order.ai_description}" <span style={{ color: BRAND.gold, fontStyle: "normal" }}>— Jacques</span>
          </div>
        )}

        {showUI && isQueued && !showRejectConfirm && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem" }}>
            <div style={{ color: "white", fontSize: "1.5rem", fontWeight: "bold" }}>START THIS PANCAKE?</div>
            <div style={{ color: BRAND.gold, fontSize: "1.25rem" }}>{order.queueNumber ? `#${order.queueNumber} · ` : ""}{order.recipientName || order.subjectName}</div>
            {order.subjectName && order.recipientName && <div style={{ color: BRAND.muted, fontSize: "0.9rem" }}>"{order.subjectName}"</div>}
            {order.submittedAt && (
              <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>
                ⏳ waiting {fmtWait(now - order.submittedAt.toMillis())}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
              {artists.map(name => (
                <button key={name} onClick={() => {
                    if (!moodCheckDone.current) { setPendingMoodCheck({ order, artist: name }); return; }
                    if (order.notes) setPendingNoteAck({ order, artist: name });
                    else startOrder(order, name);
                  }}
                  style={{ ...styles.actionButton, background: BRAND.green, minWidth: "140px" }}>
                  ▶ Start — {name}
                </button>
              ))}
            </div>
            {/* Back-out row — a queued order used to require starting the draw
                just to suspend or reject it. ⏸ Suspend is the tool for the
                absent guest (called a name, no answer): stays visible in the
                queue, zero data loss, no customer notification. ✕ Reject
                notifies the customer — reserved for actual rejections. */}
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              <button onClick={() => setSelectedOrder(null)} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted }}>Back to Queue</button>
              <button onClick={() => { suspendOrder(order); setSelectedOrder(null); }} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.muted}`, color: BRAND.muted }} title="Suspend — guest not here right now">⏸</button>
              <button onClick={() => setShowRejectConfirm(true)} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted }} title="Reject — notifies the customer">✕</button>
            </div>
          </div>
        )}

        {/* Notes acknowledgment modal — must dismiss before starting */}
        {/* Mood check — first pancake of the shift (multi-select) */}
        {pendingMoodCheck && (() => {
          const MOODS = [
            { emoji: "🔥", label: "Fired up" },
            { emoji: "😌", label: "Steady" },
            { emoji: "😴", label: "Tired" },
            { emoji: "🤩", label: "Excited" },
            { emoji: "😬", label: "Nervous" },
            { emoji: "☕", label: "Caffeinated" },
            { emoji: "🧘", label: "Zen" },
            { emoji: "🥴", label: "Rough" },
            { emoji: "🌿", label: "Elevated" },
          ];
          function submitMood(selections) {
            moodCheckDone.current = true;
            const check = { moods: selections, artist: pendingMoodCheck.artist, at: new Date().toISOString() };
            if (activeShift) updateDoc(doc(db, "shifts", activeShift.id), { moodCheck: check }).catch(() => {});
            const { order, artist } = pendingMoodCheck;
            setPendingMoodCheck(null);
            setMoodSelections([]);
            if (order.notes) setPendingNoteAck({ order, artist });
            else startOrder(order, artist);
          }
          return (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 31, padding: "1.5rem" }}>
              <div style={{ background: BRAND.card, border: `2px solid ${BRAND.gold}`, borderRadius: "16px", padding: "1.5rem", maxWidth: "380px", width: "100%", textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>How are you feeling?</div>
                <div style={{ color: BRAND.muted, fontSize: "0.8rem", marginBottom: "1rem" }}>Tap all that apply</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", marginBottom: "1rem" }}>
                  {MOODS.map(({ emoji, label }) => {
                    const active = moodSelections.includes(label);
                    return (
                      <button key={label} onClick={() => setMoodSelections(prev =>
                        prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
                      )} style={{
                        background: active ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.06)",
                        border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                        borderRadius: "12px", padding: "0.6rem 0.25rem", cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem",
                      }}>
                        <span style={{ fontSize: "1.5rem" }}>{emoji}</span>
                        <span style={{ color: active ? BRAND.gold : BRAND.muted, fontSize: "0.65rem", fontWeight: active ? "bold" : "normal" }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => submitMood(moodSelections)}
                  style={{ ...styles.actionButton, background: moodSelections.length ? BRAND.green : BRAND.raised, color: moodSelections.length ? "white" : BRAND.muted, width: "100%", fontSize: "1.05rem", padding: "0.75rem 1rem" }}>
                  {moodSelections.length ? `Let's go (${moodSelections.length})` : "Let's go"}
                </button>
                <button onClick={() => submitMood([])}
                  style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted, width: "100%", marginTop: "0.5rem", fontSize: "0.85rem" }}>
                  Skip
                </button>
              </div>
            </div>
          );
        })()}

        {pendingNoteAck && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 30, padding: "1.5rem" }}>
            <div style={{ background: BRAND.card, border: `2px solid ${BRAND.gold}`, borderRadius: "16px", padding: "1.5rem", maxWidth: "380px", width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📝</div>
              <div style={{ color: BRAND.cream, fontSize: "0.85rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem" }}>Guest Note</div>
              <div style={{ color: BRAND.gold, fontSize: "1.15rem", fontWeight: "600", lineHeight: 1.5, marginBottom: "1.25rem", fontStyle: "italic" }}>"{pendingNoteAck.order.notes}"</div>
              <button onClick={() => { startOrder(pendingNoteAck.order, pendingNoteAck.artist); setPendingNoteAck(null); }}
                style={{ ...styles.actionButton, background: BRAND.green, width: "100%", fontSize: "1.1rem", padding: "0.75rem 1rem" }}>
                Got it — Start Pancake
              </button>
              <button onClick={() => setPendingNoteAck(null)}
                style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted, width: "100%", marginTop: "0.5rem", fontSize: "0.85rem" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {reissueOrder && <ReissueTicketModal order={reissueOrder} onClose={() => setReissueOrder(null)} />}

        {/* "Still drawing?" nudge — appears in bottom third after 10min, behind action bar */}
        {showStillDrawing && isDrawing && showUI && !showRejectConfirm && (
          <div style={{ position: "absolute", bottom: "80px", left: 0, right: 0, zIndex: 8, display: "flex", justifyContent: "center", padding: "0 1rem" }}>
            <div style={{ background: "rgba(26,10,18,0.92)", border: `1px solid rgba(252,179,31,0.5)`, borderRadius: "14px", padding: "1rem 1.25rem", maxWidth: "320px", width: "100%", backdropFilter: "blur(8px)", textAlign: "center" }}>
              <p style={{ color: BRAND.cream, fontSize: "0.9rem", margin: "0 0 0.75rem" }}>Still drawing?</p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button onClick={() => { stillDrawingDismissedAt.current = Date.now(); setShowStillDrawing(false); }}
                  style={{ ...styles.actionButton, flex: 1, background: BRAND.card, fontSize: "0.85rem" }}>
                  Yes, keep going
                </button>
                <button onClick={() => { setShowStillDrawing(false); markCooking(order); }}
                  style={{ ...styles.actionButton, flex: 1, background: BRAND.orange, fontSize: "0.85rem" }}>
                  🍳 Done Drawing
                </button>
              </div>
            </div>
          </div>
        )}

        {showUI && (isDrawing || isCooking || isFlipping) && !showRejectConfirm && (
          <div style={{ display: "flex", gap: "0.75rem", padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
            {isDrawing && <button onClick={() => markCooking(order)} style={{ ...styles.actionButton, flex: 1, background: "#f5a623", color: BRAND.dark, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>🍳 On the Griddle</button>}
            {isCooking && <button onClick={() => markFlipping(order)} style={{ ...styles.actionButton, flex: 1, background: BRAND.orange, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>🔥 Flip!</button>}
            {isFlipping && !cookLocked && (
              <button onClick={() => markOffGriddle(order)} style={{ ...styles.actionButton, flex: 1, background: BRAND.gold, color: BRAND.dark, fontWeight: 800, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>✅ Off the Griddle</button>
            )}
            {isFlipping && cookLocked && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", color: BRAND.dim, fontFamily: "monospace", fontSize: "0.9rem", fontWeight: "bold" }}>🔥 {fmtMs(order.cookTimeMs)} <span style={{ color: BRAND.muted, fontFamily: FONT.body, fontWeight: "normal" }}>locked</span></div>
            )}
            {isFlipping && (
              <button onClick={() => setShowFinishWithoutPhotoConfirm(true)} style={{ ...styles.actionButton, background: "#2a4a2a", border: "1px solid #3a6a3a", color: "#6fcc6f", ...(isLandscape && { padding: "0.6rem 1rem" }) }}>Finish Without Photo</button>
            )}
            {!isFlipping && <button onClick={() => suspendOrder(order)} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.muted}`, color: BRAND.muted, padding: isLandscape ? "0.6rem" : "0.75rem" }} title="Suspend">⏸</button>}
            <button onClick={() => setShowRejectConfirm(true)} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted, padding: isLandscape ? "0.6rem" : "0.75rem" }}>✕</button>
          </div>
        )}

        {/* Cooking/Flipping — back-to-queue bar. (Gut-feel vibe tagging moved to
            the Library; it was never used live at the booth.) */}
        {showUI && (isCooking || isFlipping) && !showFinishWithoutPhotoConfirm && !showRejectConfirm && (
          <div style={{ padding: "0.5rem 1rem", background: "#060c20", borderTop: `1px solid ${BRAND.dark}`, flexShrink: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button onClick={() => { setSelectedOrder(null); setShowRejectConfirm(false); setShowGridOptions(false); }} style={{ ...styles.actionButton, background: BRAND.card, padding: isLandscape ? "0.5rem 0.75rem" : "0.6rem 0.75rem", marginLeft: "auto", fontSize: "0.8rem" }}>← Queue</button>
          </div>
        )}

        {showUI && isSuspended && !showRejectConfirm && (
          <div style={{ display: "flex", gap: "0.75rem", padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
            <button onClick={() => { setSelectedOrder(null); setShowRejectConfirm(false); setShowGridOptions(false); }} style={{ ...styles.actionButton, background: BRAND.card, padding: isLandscape ? "0.6rem 1rem" : "0.75rem 1rem" }}>← Queue</button>
            <button onClick={() => resumeOrder(order)} style={{ ...styles.actionButton, flex: 1, background: BRAND.blue, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>▶ Resume</button>
            <button onClick={() => setShowRejectConfirm(true)} style={{ ...styles.actionButton, background: "transparent", border: `1px solid ${BRAND.dim}`, color: BRAND.muted, padding: isLandscape ? "0.6rem" : "0.75rem" }}>✕</button>
          </div>
        )}

        {showUI && isFlipping && showFinishWithoutPhotoConfirm && (
          <div style={{ padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#1a1a0a", borderTop: "1px solid #443", display: "flex", flexDirection: "column", gap: isLandscape ? "0.4rem" : "0.75rem", flexShrink: 0 }}>
            <div style={{ color: "rgba(255,255,255,0.7)", textAlign: "center" }}>Mark done without a photo? Customer can't see a keepsake.</div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => finishWithoutPhoto(order)} style={{ ...styles.actionButton, flex: 1, background: BRAND.green, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>✅ Finish</button>
              <button onClick={() => setShowFinishWithoutPhotoConfirm(false)} style={{ ...styles.actionButton, flex: 1, background: BRAND.card, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Content piece — Preserve / Discard instead of Hand Off after photo step */}
        {showUI && order.state === "ready_to_deliver" && order.priority === "content" && !showRejectConfirm && (
          <div style={{ padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, display: "flex", gap: "0.5rem", flexShrink: 0 }}>
            <button onClick={() => { setSelectedOrder(null); setShowRejectConfirm(false); setShowGridOptions(false); }} style={{ ...styles.actionButton, background: BRAND.card, padding: isLandscape ? "0.6rem 1rem" : "0.75rem 1rem" }}>← Queue</button>
            <button onClick={() => markContentDone(order, false)} style={{ ...styles.actionButton, flex: 1, background: BRAND.pink, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>🗑 Discard</button>
            <button onClick={() => markContentDone(order, true)} style={{ ...styles.actionButton, flex: 1, background: BRAND.gold, color: BRAND.dark, fontWeight: 800, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>🖌 Preserve</button>
          </div>
        )}

        {/* Catch-all back button for states with no other action bar */}
        {showUI && !isQueued && !isDrawing && !isCooking && !isFlipping && !isSuspended && !showRejectConfirm && !(order.state === "ready_to_deliver" && order.priority === "content") && (
          <div style={{ padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
            <button onClick={() => { setSelectedOrder(null); setShowRejectConfirm(false); setShowGridOptions(false); }} style={{ ...styles.actionButton, width: "100%", background: BRAND.card, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>← Back to Queue</button>
          </div>
        )}

        {showUI && showRejectConfirm && (
          <div style={{ padding: isLandscape ? "0.5rem 1rem" : "1rem", background: "#1a0000", borderTop: "1px solid #500", display: "flex", flexDirection: "column", gap: isLandscape ? "0.4rem" : "0.75rem", flexShrink: 0 }}>
            <div style={{ color: "white", textAlign: "center" }}>Reject this order? Customer will be notified.</div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => rejectOrder(order)} style={{ ...styles.actionButton, flex: 1, background: BRAND.red, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>Reject</button>
              <button onClick={() => setShowRejectConfirm(false)} style={{ ...styles.actionButton, flex: 1, background: BRAND.card, ...(isLandscape && { padding: "0.6rem 1rem" }) }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── FLIP COUNTDOWN OVERLAY ── */}
        {flipCountdown != null && (
          <FlipCountdownOverlay
            startedAt={flipCountdown}
            onDone={() => setFlipCountdown(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ background: BRAND.dark, minHeight: "100vh", fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      {/* Top bar: flexWrap + rowGap so ShiftManager HUD and queue counter
          can break onto their own row on iPhone without pushing Close Shift
          off-screen. minWidth:0 on the eventName block lets it truncate
          instead of forcing the layout to overflow horizontally. */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", rowGap: "0.55rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.1rem" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem" }}>🥞 Queue</div>
          {user?.email && <div style={{ color: BRAND.dim, fontSize: "0.7rem" }}>{user.email}</div>}
        </div>
        {activeShift?.eventName && (
          <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem", flex: 1, minWidth: 0, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {activeShift.eventName}
          </div>
        )}
        {/* Booth code — the handle guests type and OBS browser sources carry.
            Only shown while concurrent queues exist, so a solo market's header
            is unchanged. */}
        {isConcurrent && activeShift?.joinCode && (
          <ShiftChip shift={activeShift} isConcurrent={isConcurrent} onChange={() => setShowShiftPicker(true)} />
        )}
        {/* Opening a second concurrent queue is an explicit act — the system
            cannot tell "one event is running" apart from "my event hasn't
            started yet", so the operator declares it. */}
        <button
          onClick={() => setOpenAnother(true)}
          style={{ ...styles.hudButton, fontSize: "0.75rem" }}
          title="Open a separate queue for a different event happening at the same time"
        >＋ New queue</button>
        <button onClick={() => setShowFlashRefs(true)} style={{ ...styles.hudButton, fontSize: "0.75rem" }} title="Open a flash design as a drawing reference — no ticket">⚡ Refs</button>
        {activeShift && (
          <button onClick={() => setShowPartyList(true)} style={{ ...styles.hudButton, fontSize: "0.75rem" }} title="Batch-add a list of guests to the staging tray">📋 Party list</button>
        )}
        <button onClick={() => navigate("/menu")} style={{ ...styles.hudButton, fontSize: "0.75rem" }}>← Menu</button>
        <ShiftManager activeShift={activeShift} onShiftOpen={(shift) => setActiveShift(shift)} onShiftClose={() => setActiveShift(null)} user={user} />
        <div style={{ color: BRAND.dim, fontSize: "0.75rem", textAlign: "right" }}>
          <div>{orders.length} active</div>
          {orders.length > 0 && fmtWaitTime(computeQueueWait(orders, now, activeShift)) && (
            <div style={{ color: BRAND.gold, fontWeight: "600", fontSize: "0.8rem" }}>
              {fmtWaitTime(computeQueueWait(orders, now, activeShift))}
            </div>
          )}
        </div>
      </div>

      <QueueTabs active={activeTab} onSwitch={setQueueTab} studioCount={studioOrders.length} />

      {/* Hidden picker for attaching reference images to staged rows */}
      <input ref={attachInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAttachImageFile} />

      {/* 📋 Party list modal — batch-add guests to the staging tray */}
      {showPartyList && (
        <div
          onClick={() => setShowPartyList(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 1rem 1rem", overflowY: "auto" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: "460px", background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "14px", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.7rem", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ color: "white", fontSize: "1rem", fontWeight: 700 }}>📋 Party list</div>
              <button onClick={() => setShowPartyList(false)} aria-label="Close" style={{ background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.dim, width: "32px", height: "32px", borderRadius: "999px", fontSize: "1rem", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.78rem", lineHeight: 1.45 }}>
              One guest per line — <span style={{ color: BRAND.muted }}>"Name — what they want"</span>.
              Everyone lands in the 📥 Staged tray; add reference images from each row's ⋯ menu, then promote when it's their turn.
            </div>
            <textarea
              value={partyListText}
              onChange={(e) => setPartyListText(e.target.value)}
              rows={9}
              placeholder={"1. Emma — unicorn\n2. Jack — fire truck\n3. Priya — soccer ball"}
              style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: `1px solid ${BRAND.border}`, borderRadius: "10px", color: "white", fontSize: "0.95rem", lineHeight: 1.5, padding: "0.7rem 0.8rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
            />
            <button
              onClick={submitPartyList}
              disabled={partyListSubmitting || !partyListText.trim()}
              style={{ background: partyListText.trim() ? BRAND.gold : BRAND.border, color: BRAND.dark, border: "none", borderRadius: "10px", padding: "0.75rem", fontWeight: "bold", fontSize: "0.95rem", cursor: partyListText.trim() ? "pointer" : "default", opacity: partyListSubmitting ? 0.7 : 1 }}
            >
              {partyListSubmitting ? "Adding…" : "Add to staging tray"}
            </button>
          </div>
        </div>
      )}

      {(activeShift?.shiftBestCustomDrawMs || activeShift?.shiftBestFlashDrawMs) && (
        <div style={{ padding: "0.3rem 1rem", background: "#0a1230", borderBottom: `1px solid ${BRAND.dark}`, display: "flex", gap: "1.5rem", alignItems: "center", flexWrap: "wrap", fontFamily: "monospace", fontSize: "0.72rem" }}>
          <span style={{ color: BRAND.muted, fontSize: "0.65rem", letterSpacing: "0.07em" }}>SHIFT BEST</span>
          {activeShift.shiftBestCustomDrawMs && (
            <span>
              <span style={{ color: BRAND.dim }}>Custom </span>
              <span style={{ color: BRAND.muted }}>✏️ {fmtMs(activeShift.shiftBestCustomDrawMs)}</span>
              {activeShift.shiftBestCustomTotalMs && <span style={{ color: BRAND.muted }}> ⏱ {fmtMs(activeShift.shiftBestCustomTotalMs)}</span>}
            </span>
          )}
          {activeShift.shiftBestFlashDrawMs && (
            <span>
              <span style={{ color: BRAND.dim }}>Flash </span>
              <span style={{ color: BRAND.muted }}>✏️ {fmtMs(activeShift.shiftBestFlashDrawMs)}</span>
              {activeShift.shiftBestFlashTotalMs && <span style={{ color: BRAND.muted }}> ⏱ {fmtMs(activeShift.shiftBestFlashTotalMs)}</span>}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: BRAND.dim, padding: "3rem", textAlign: "center" }}>Loading queue...</div>
      ) : (() => {
        const deliveryOrders = orders.filter(o => o.state === "ready_to_deliver");
        const mainOrders     = orders.filter(o => o.state !== "ready_to_deliver" && o.queue !== "standby");
        const standbyOrders  = orders.filter(o => o.queue === "standby" && o.state === "queued");
        // Merge pre-uploads (booking listener) + party-list rows (shift
        // listener); a party-list row on a booking-bound shift matches both.
        const stagedAll = (() => {
          const m = new Map();
          [...stagedOrders, ...stagedShiftOrders].forEach(o => m.set(o.id, o));
          return [...m.values()].sort((a, b) =>
            ((a.stagedAt || a.createdAt)?.toMillis?.() ?? 0) - ((b.stagedAt || b.createdAt)?.toMillis?.() ?? 0));
        })();
        return (
          <>
            {stagedAll.length > 0 && (
              <div style={{ padding: stagingTrayOpen ? "0.75rem 1rem" : "0.55rem 1rem", borderBottom: `1px solid ${BRAND.dark}`, background: "rgba(252,179,31,0.08)" }}>
                <button
                  type="button"
                  onClick={() => setStagingTrayOpen(v => !v)}
                  style={{
                    width: "100%", background: "transparent", border: "none", padding: 0,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: stagingTrayOpen ? "0.6rem" : 0, cursor: "pointer",
                  }}
                >
                  <span style={{ color: BRAND.gold, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
                    {stagingTrayOpen ? "▾" : "▸"} 📥 Staged — {stagedAll.length}
                  </span>
                  <span style={{ color: BRAND.dim, fontSize: "0.6rem" }}>
                    {stagingTrayOpen ? "Tap Promote to add to queue" : "Tap to expand"}
                  </span>
                </button>
                {stagingTrayOpen && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {stagedAll.map(order => (
                      <StagedOrderRow
                        key={order.id}
                        order={order}
                        busy={promotingOrderId === order.id}
                        onPromote={() => promoteStagedOrder(order)}
                        onTrash={() => trashStagedOrder(order)}
                        onToggleHold={() => toggleStagedHold(order)}
                        onAttachImage={() => requestAttachImage(order)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {deliveryOrders.length > 0 && (
              <div style={{ padding: "0.75rem 1rem", borderBottom: `1px solid ${BRAND.dark}`, background: "rgba(112,190,68,0.12)" }}>
                <div style={{ color: "#00e5cc", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.6rem" }}>
                  Ready to Hand Off — {deliveryOrders.length}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {deliveryOrders.map(order => (
                    <HandOffRow key={order.id} order={order} onHandOff={() => markDelivered(order)} />
                  ))}
                </div>
              </div>
            )}
            {mainOrders.length === 0 && standbyOrders.length === 0 ? (
              <div style={{ color: BRAND.dim, padding: "3rem", textAlign: "center", fontSize: "1rem" }}>
                {activeShift?.isQueueOpen ? "Queue is empty. Waiting for orders..." : "Queue closed. Finishing remaining orders."}
              </div>
            ) : (
              <>
                {mainOrders.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem", padding: "1rem", overflowY: "auto" }}>
                    {mainOrders.map(order => <OrderCard key={order.id} order={order} onTap={() => setSelectedOrder(order)} onToggleMark={() => toggleMeditationMark(order)} now={now} designMap={designMap} />)}
                  </div>
                )}
                {standbyOrders.length > 0 && (
                  <div style={{ borderTop: `1px solid ${BRAND.dark}` }}>
                    <div style={{ padding: "0.6rem 1rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" }}>
                      <div style={{ color: BRAND.sky, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                        📺 Standby — {standbyOrders.length}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <div style={{ color: BRAND.dim, fontSize: "0.6rem" }}>Tap twice to promote</div>
                        <button
                          onClick={() => admitAllFromStandby(standbyOrders.length)}
                          style={{
                            background: "rgba(74,164,225,0.12)", border: `1px solid ${BRAND.sky}`,
                            borderRadius: "8px", padding: "0.3rem 0.65rem",
                            color: BRAND.sky, fontSize: "0.68rem", fontWeight: 700, cursor: "pointer",
                          }}
                        >
                          ⬆ Admit all
                        </button>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem", padding: "0 1rem 1rem", opacity: 0.75 }}>
                      {standbyOrders.map(order => {
                        const armed = armedStandbyId === order.id;
                        const promoting = promotingStandbyIds.includes(order.id);
                        return (
                          <div key={order.id} style={{ position: "relative", borderRadius: "12px", outline: armed && !promoting ? `3px solid ${BRAND.gold}` : "none", opacity: promoting ? 0.6 : 1 }}>
                            <OrderCard order={order} now={now} designMap={designMap}
                              onTap={() => {
                                if (promoting) return;
                                if (armed) { setArmedStandbyId(null); promoteFromStandby(order); }
                                else setArmedStandbyId(order.id);
                              }} />
                            {promoting && (
                              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(13,24,51,0.55)", borderRadius: "12px", color: BRAND.gold, fontWeight: 800, fontSize: "0.72rem", zIndex: 2, pointerEvents: "none" }}>
                                ⏳ Promoting…
                              </div>
                            )}
                            {armed && !promoting && (
                              <div style={{ position: "absolute", left: 0, right: 0, bottom: "-1.2rem", textAlign: "center", color: BRAND.gold, fontSize: "0.62rem", fontWeight: 800, pointerEvents: "none" }}>
                                👆 tap again to promote
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Completed + rejected toggles */}
            <div style={{ borderTop: `1px solid ${BRAND.dark}`, padding: "0.6rem 1rem 0.4rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button onClick={() => setShowCompleted(v => !v)} style={{
                background: showCompleted ? "rgba(239,83,126,0.12)" : "transparent",
                border: `1px solid ${showCompleted ? BRAND.pink : BRAND.border}`,
                borderRadius: "8px", padding: "0.45rem 0.75rem",
                color: showCompleted ? BRAND.pink : BRAND.dim, fontSize: "0.78rem", fontWeight: "bold", cursor: "pointer",
              }}>
                {showCompleted ? `✓ Completed (${completedOrders.length})` : "Show Completed"}
              </button>
              <button onClick={() => setShowRejected(v => !v)} style={{
                background: showRejected ? "rgba(245,118,0,0.12)" : "transparent",
                border: `1px solid ${showRejected ? BRAND.orange : BRAND.border}`,
                borderRadius: "8px", padding: "0.45rem 0.75rem",
                color: showRejected ? BRAND.orange : BRAND.dim, fontSize: "0.78rem", fontWeight: "bold", cursor: "pointer",
              }}>
                {showRejected ? `✕ Rejected (${rejectedOrders.length})` : "Show Rejected"}
              </button>
            </div>
            {showRejected && (
              <div style={{ padding: "0 1rem 1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {rejectedOrders.length === 0 ? (
                  <div style={{ color: BRAND.dim, fontSize: "0.78rem", padding: "0.5rem 0" }}>No rejected orders this shift.</div>
                ) : rejectedOrders.map(order => (
                  <div key={order.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(245,118,0,0.05)", borderRadius: "10px", padding: "0.5rem 0.75rem", border: `1px solid ${BRAND.dark}` }}>
                    {(order.croppedImageUrl || order.imageUrl) && (
                      <img src={order.croppedImageUrl || order.imageUrl} alt="" style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontWeight: "bold", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {order.queueNumber ? `#${order.queueNumber} · ` : ""}{order.recipientName || order.subjectName}
                      </div>
                      <div style={{ color: BRAND.dim, fontSize: "0.7rem" }}>
                        {order.orderType === "flash" ? "⚡ Flash" : order.orderType === "freestyle_flash" ? "⚡ Freestyle" : "🎨 Custom"}
                        {order.rejectedAt && <span style={{ marginLeft: "0.5rem" }}>rejected {fmtWait(now - order.rejectedAt.toMillis())} ago</span>}
                      </div>
                    </div>
                    <button onClick={() => restoreRejected(order)} style={{ background: BRAND.blue, border: "none", borderRadius: "8px", padding: "0.4rem 0.7rem", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                      ↩ Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
            {showCompleted && completedOrders.length > 0 && (
              <div style={{ padding: "0 1rem 1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {completedOrders.map(order => (
                  <div key={order.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(255,255,255,0.04)", borderRadius: "10px", padding: "0.5rem 0.75rem", border: `1px solid ${BRAND.dark}` }}>
                    {(order.photo_cutout_url || order.photo_url || order.croppedImageUrl) && (
                      <img src={order.photo_cutout_url || order.photo_url || order.croppedImageUrl} alt="" style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontWeight: "bold", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {order.queueNumber ? `#${order.queueNumber} · ` : ""}{order.recipientName || order.subjectName}
                      </div>
                      <div style={{ color: BRAND.dim, fontSize: "0.7rem" }}>
                        {order.orderType === "flash" ? "⚡ Flash" : "🎨 Custom"}
                        {order.comboKey && COMBOS[order.comboKey] && <span style={{ color: BRAND.gold, marginLeft: "0.5rem" }}>{COMBOS[order.comboKey].emoji} {COMBOS[order.comboKey].label}</span>}
                        {order.autoClosedWithShift && <span style={{ color: BRAND.orange, marginLeft: "0.5rem" }}>auto-closed</span>}
                      </div>
                      {(order.addOns || []).length > 0 && (
                        <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginTop: "0.25rem" }}>
                          {order.addOns.map((a, i) => (
                            <button key={i} onClick={() => toggleAddOnFulfilled(order, i)}
                              title={a.fulfilledAt ? "Handed over — tap to un-mark" : "Tap when handed over"}
                              style={a.fulfilledAt
                                ? { background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "6px", padding: "0.2rem 0.45rem", color: BRAND.dim, fontSize: "0.65rem", cursor: "pointer" }
                                : { background: "rgba(252,179,31,0.15)", border: `1px solid ${BRAND.gold}`, borderRadius: "6px", padding: "0.2rem 0.45rem", color: BRAND.gold, fontSize: "0.65rem", fontWeight: "bold", cursor: "pointer" }}>
                              {addOnLabel(a)}{a.fulfilledAt ? " ✓" : " — owed"}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!order.comboKey && order.orderType !== "flash" && COMBO_ORDER.map((key) => (
                      <button key={key} onClick={() => stampCombo(order, key)}
                        title={`Stamp ${COMBOS[key].label} (rang on POS)`}
                        style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.35rem 0.5rem", color: BRAND.dim, fontSize: "0.75rem", cursor: "pointer", flexShrink: 0 }}>
                        {COMBOS[key].emoji}
                      </button>
                    ))}
                    <button onClick={() => setReissueOrder(order)} style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.35rem 0.6rem", color: BRAND.gold, fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                      🎟
                    </button>
                    <button
                      onClick={async () => {
                        const next = !order.isPreservation;
                        await updateDoc(doc(db, "orders", order.id), {
                          isPreservation: next,
                          aftercare: next ? "preserve" : "deliver",
                          ...(next && !order.preservationStatus ? { preservationStatus: "drying" } : {}),
                          ...(!next ? { preservationStatus: null } : {}),
                          updatedAt: serverTimestamp(),
                        });
                      }}
                      style={{
                        background: order.isPreservation ? "rgba(252,179,31,0.15)" : "transparent",
                        border: `1px solid ${order.isPreservation ? BRAND.gold : BRAND.border}`,
                        borderRadius: "8px", padding: "0.35rem 0.6rem",
                        color: order.isPreservation ? BRAND.gold : BRAND.dim,
                        fontSize: "0.7rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0,
                      }}
                      title={order.isPreservation ? "Preservation active — tap to remove" : "Tap to enter preservation pipeline"}
                    >
                      🖌 {order.isPreservation ? "✓" : ""}
                    </button>
                    <button onClick={() => { if (window.confirm(`Redo ${order.recipientName || order.subjectName}'s order? It will return to the queue.`)) redoOrder(order); }} style={{ background: BRAND.orange, border: "none", borderRadius: "8px", padding: "0.4rem 0.7rem", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                      ↩ Redo
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      })()}

      {/* Flash reference picker — tap a design to open it in the work view as a
          view-only reference (gridded + mirrored, no GTS order/token). For
          volume events where flash repeats don't each need their own ticket. */}
      {showFlashRefs && (
        <div onClick={() => setShowFlashRefs(false)} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "16px", padding: "1rem", width: "100%", maxWidth: "720px", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.75rem", marginBottom: "0.85rem" }}>
              <div>
                <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.05rem" }}>⚡ Flash Reference</div>
                <div style={{ color: BRAND.dim, fontSize: "0.72rem" }}>Tap to open gridded + mirrored — view only, no ticket or queue number</div>
              </div>
              <button onClick={() => setShowFlashRefs(false)} style={{ ...styles.hudButton, flexShrink: 0 }}>✕</button>
            </div>
            {flashDesigns.length === 0 ? (
              <div style={{ color: BRAND.dim, padding: "2rem", textAlign: "center", fontSize: "0.9rem" }}>No flash designs loaded yet…</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "0.6rem" }}>
                {flashDesigns.map(d => (
                  <button key={d.id}
                    onClick={() => { setShowFlashRefs(false); setMirrored(true); setSelectedOrder(makeFlashRefOrder(d)); }}
                    style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.5rem 0.35rem 0.4rem", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.35rem" }}>
                    <img src={d.imageUrl} alt={d.label} style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "contain", display: "block" }} />
                    <span style={{ color: BRAND.cream, fontSize: "0.72rem", fontWeight: 700, lineHeight: 1.2, textAlign: "center" }}>{d.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Even-odd path for viewBox 0 0 100 100: full square minus inscribed circle = corner region
const CARD_CORNER_PATH = "M 0 0 H 100 V 100 H 0 Z M 50 6 A 44 44 0 1 0 50 94 A 44 44 0 1 0 50 6 Z";

function OrderCard({ order, onTap, onToggleMark, now, designMap }) {
  const waitDisplay  = (order.submittedAt && now)
    ? fmtWait(now - order.submittedAt.toMillis())
    : null;
  const isSuspended  = order.state === "suspended";
  const cookStart    = order.cookingStartedAt || order.flippedAt;
  const cookingMs    = (order.state === "cooking" || order.state === "flipping") && cookStart ? now - cookStart.toMillis() : null;
  // Nudge only while the cook clock is still running — once "Off the Griddle"
  // locks cookTimeMs the pancake is just waiting on a photo, and pulsing it
  // forever reads as a stuck timer (Balloons Over Marine 2026-09-26).
  const doneNudge    = cookingMs != null && cookingMs > 90000 && order.cookTimeMs == null;
  const nudgePulse   = doneNudge && Math.floor(now / 600) % 2 === 0;
  const borderColor  = isSuspended ? BRAND.border
    : doneNudge ? (nudgePulse ? BRAND.orange : "#7a3800")
    : (STATE_COLORS[order.state] ?? BRAND.border);
  return (
    <div
      onClick={onTap}
      style={{ background: BRAND.panel, borderRadius: "12px", overflow: "hidden", cursor: "pointer", border: `2px solid ${borderColor}`, transition: "border-color 0.5s", opacity: isSuspended ? 0.5 : 1 }}
    >
      {order.queueNumber && (
        <div style={{ background: STATE_COLORS[order.state] ?? BRAND.border, color: order.state === "queued" ? BRAND.dark : "white", textAlign: "center", fontWeight: "bold", fontSize: "1.1rem", padding: "0.25rem 0" }}>
          #{order.queueNumber}
        </div>
      )}
      <div style={{ width: "100%", aspectRatio: "1 / 1", background: BRAND.panel, overflow: "hidden", position: "relative" }}>
        {onToggleMark && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMark(); }}
            title="Mark for meditation"
            style={{
              position: "absolute", top: "6px", right: "6px", zIndex: 2,
              background: order.meditationMark ? "rgba(252,179,31,0.92)" : "rgba(0,0,0,0.55)",
              color: order.meditationMark ? BRAND.dark : "rgba(255,255,255,0.85)",
              border: "none", borderRadius: "999px",
              width: "28px", height: "28px",
              fontSize: "0.85rem", lineHeight: 1, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ✨
          </button>
        )}
        {order.photo_url ? (
          <img src={order.photo_url} alt="Final pancake" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : order.croppedImageUrl ? (
          <img src={order.croppedImageUrl} alt={order.recipientName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : order.orderType === "flash" && order.designId && designMap[order.designId] ? (
          <img src={designMap[order.designId].imageUrl} alt={order.designLabel} style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px", boxSizing: "border-box" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: BRAND.border, fontSize: "2rem" }}>⚡</div>
        )}
      </div>
      <div style={{ padding: "0.5rem 0.75rem" }}>
        <div style={{ color: BRAND.dim, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.07em", lineHeight: 1 }}>For</div>
        <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem", marginBottom: "0.2rem" }}>{order.recipientName}</div>
        {order.subjectName && (
          <div style={{ color: BRAND.cream, fontSize: "0.75rem", marginBottom: "0.2rem" }}>{order.subjectName}</div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ display: "inline-block", background: STATE_COLORS[order.state] ?? BRAND.border, color: order.state === "queued" ? BRAND.dark : "white", fontSize: "0.6rem", fontWeight: "bold", padding: "0.2rem 0.5rem", borderRadius: "4px", letterSpacing: "0.05em" }}>
            {STATE_LABELS[order.state] ?? order.state}
          </div>
          {waitDisplay !== null && (
            <div style={{ color: BRAND.dim, fontSize: "0.65rem" }}>⏳ {waitDisplay}</div>
          )}
          {order.restoredFromRejection && order.state === "queued" && (
            <div style={{ color: BRAND.sky, fontSize: "0.6rem", fontWeight: "bold", border: `1px solid ${BRAND.sky}`, borderRadius: "4px", padding: "0.15rem 0.4rem" }} title="Restored from rejected — keeps its original number, so it may sort ahead">
              ↩ restored
            </div>
          )}
          {/* Combo add-on obligations — goods that must change hands at
              delivery. Gold pill = still owed; dim ✓ = handed over. */}
          {(order.addOns || []).map((a, i) => (
            <div key={i} style={a.fulfilledAt
              ? { color: BRAND.dim, fontSize: "0.6rem", padding: "0.15rem 0.4rem" }
              : { color: BRAND.gold, fontSize: "0.6rem", fontWeight: "bold", border: `1px solid ${BRAND.gold}`, borderRadius: "4px", padding: "0.15rem 0.4rem" }}
              title={a.fulfilledAt ? "Handed over" : (ADD_ONS[a.type]?.fulfillHint || "Owed at delivery")}>
              {addOnLabel(a)}{a.fulfilledAt ? " ✓" : ""}
            </div>
          ))}
        </div>
        {order.artistName && order.state !== "queued" && (
          <div style={{ color: BRAND.dim, fontSize: "0.65rem", marginTop: "0.2rem" }}>🎨 {order.artistName}</div>
        )}
        {/* Repeat-request signal (server-stamped at creation) — a neutral fact
            for the promote decision, deliberately unstyled: known and fine
            cases exist (shared phones get passed around). Never enforcement. */}
        {order.deviceRequestSeq != null && (
          <div style={{ color: BRAND.dim, fontSize: "0.62rem", marginTop: "0.2rem" }}>
            {ordinal(order.deviceRequestSeq)} request from this device
          </div>
        )}
        {(() => {
          const isActive = order.state === "drawing" || order.state === "cooking" || order.state === "flipping" || order.state === "ready_to_deliver";
          const drawLocked  = order.drawingTimeMs != null;
          const cookLocked  = order.cookTimeMs    != null;
          const liveDrawMs  = order.state === "drawing" && order.startedAt ? now - order.startedAt.toMillis() : null;
          const cookStart   = order.cookingStartedAt || order.flippedAt;
          const liveFlipMs  = !cookLocked && (order.state === "cooking" || order.state === "flipping") && cookStart ? now - cookStart.toMillis() : null;
          if (!drawLocked && !cookLocked && liveDrawMs == null) return null;
          return (
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.3rem", fontFamily: "monospace", fontSize: "0.6rem", flexWrap: "wrap" }}>
              {liveDrawMs != null && <span style={{ color: BRAND.blue }}>✏️ {fmtStopwatch(liveDrawMs)}</span>}
              {drawLocked && <span style={{ color: BRAND.dim }}>✏️ {fmtMs(order.drawingTimeMs)}</span>}
              {liveFlipMs != null && <span style={{ color: BRAND.orange }}>🔥 {fmtStopwatch(liveFlipMs)}</span>}
              {cookLocked && <span style={{ color: BRAND.dim }}>🔥 {fmtMs(order.cookTimeMs)}</span>}
            </div>
          );
        })()}
        {order.notes ? <div style={{ color: BRAND.gold, fontSize: "0.75rem", fontWeight: "600", marginTop: "0.3rem", lineHeight: 1.3 }}>📝 {order.notes.slice(0, 60)}{order.notes.length > 60 ? "…" : ""}</div> : null}
      </div>
    </div>
  );
}

// ── STAGED ORDER ROW ─────────────────────────────────────────────────────────
// One row in the pre-uploaded staging tray (above the live queue when the
// active shift is bound to a booking that has pre-uploads). Promote calls
// the server-side promoteStagedOrder which atomically assigns queueNumber
// + designSequence and flips state→queued. Trash/Hold are direct staff
// writes against the order doc.
function StagedOrderRow({ order, busy, onPromote, onTrash, onToggleHold, onAttachImage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const name      = order.recipientName || order.subjectName || order.customerName || "Unnamed";
  const thumbSrc  = order.photo_cutout_url || order.croppedImageUrl || order.imageUrl || order.photo_url;
  const designLbl = order.designLabel
    || (order.orderType === "freestyle_flash" || order.isFreestyle ? "Freestyle" : null)
    || (order.orderType === "custom" ? "Custom" : null)
    || order.designId
    || "—";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(252,179,31,0.08)", borderRadius: "10px", padding: "0.5rem 0.75rem", border: `1px solid ${order.holdIndefinitely ? BRAND.gold : "rgba(252,179,31,0.3)"}`, position: "relative" }}>
      {thumbSrc ? (
        <img src={thumbSrc} alt={name} style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }} />
      ) : (
        <div style={{ width: "52px", height: "52px", borderRadius: "8px", flexShrink: 0, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", color: BRAND.dim, fontSize: "1.4rem" }}>📥</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
          {order.holdIndefinitely && <span title="Held for rescheduled event" style={{ color: BRAND.gold, fontSize: "0.72rem" }}>🔒</span>}
        </div>
        <div style={{ color: BRAND.dim, fontSize: "0.72rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{designLbl}</div>
      </div>
      <button
        onClick={onPromote}
        disabled={busy || order.holdIndefinitely}
        title={order.holdIndefinitely ? "Held for a future event — release the hold to promote" : undefined}
        style={{ background: (busy || order.holdIndefinitely) ? BRAND.border : BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.55rem 1rem", fontWeight: "bold", fontSize: "0.85rem", cursor: (busy || order.holdIndefinitely) ? "default" : "pointer", flexShrink: 0, whiteSpace: "nowrap", opacity: (busy || order.holdIndefinitely) ? 0.7 : 1 }}
      >
        {busy ? "…" : order.holdIndefinitely ? "🔒 Held" : "Promote →"}
      </button>
      <button
        onClick={() => setMenuOpen(v => !v)}
        aria-label="More"
        style={{ background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.dim, width: "32px", height: "32px", borderRadius: "8px", fontSize: "1rem", cursor: "pointer", flexShrink: 0, lineHeight: 1 }}
      >
        ⋯
      </button>
      {menuOpen && (
        <div
          onMouseLeave={() => setMenuOpen(false)}
          style={{ position: "absolute", top: "100%", right: "0.5rem", marginTop: "0.25rem", background: "#142548", border: `1px solid ${BRAND.border}`, borderRadius: "8px", boxShadow: "0 6px 18px rgba(0,0,0,0.4)", zIndex: 10, minWidth: "180px", overflow: "hidden" }}
        >
          {onAttachImage && (
            <button
              onClick={() => { setMenuOpen(false); onAttachImage(); }}
              style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: "white", fontSize: "0.82rem", padding: "0.55rem 0.85rem", cursor: "pointer" }}
            >
              📷 {thumbSrc ? "Replace reference image" : "Add reference image"}
            </button>
          )}
          <button
            onClick={() => { setMenuOpen(false); onToggleHold(); }}
            style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", borderTop: onAttachImage ? `1px solid ${BRAND.border}` : "none", color: order.holdIndefinitely ? BRAND.gold : "white", fontSize: "0.82rem", padding: "0.55rem 0.85rem", cursor: "pointer" }}
          >
            {order.holdIndefinitely ? "🔓 Release hold" : "🔒 Hold for rescheduled event"}
          </button>
          <button
            onClick={() => { setMenuOpen(false); onTrash(); }}
            style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", borderTop: `1px solid ${BRAND.border}`, color: BRAND.pink, fontSize: "0.82rem", padding: "0.55rem 0.85rem", cursor: "pointer" }}
          >
            🗑 Move to trash
          </button>
        </div>
      )}
    </div>
  );
}

function HandOffRow({ order, onHandOff }) {
  const name     = order.recipientName || order.subjectName || order.customerName;
  const thumbSrc = order.photo_cutout_url || order.photo_url || order.croppedImageUrl;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(112,190,68,0.12)", borderRadius: "10px", padding: "0.5rem 0.75rem", border: "1px solid rgba(112,190,68,0.3)" }}>
      {thumbSrc && (
        <img src={thumbSrc} alt={name} style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
        {order.queueNumber && <div style={{ color: "#00e5cc", fontSize: "0.75rem" }}>#{order.queueNumber}</div>}
        {unfulfilledAddOns(order).length > 0 && (
          <div style={{ color: BRAND.gold, fontSize: "0.72rem", fontWeight: "bold" }}>
            + {unfulfilledAddOns(order).map(addOnLabel).join(" · ")}
          </div>
        )}
      </div>
      <button
        onClick={onHandOff}
        style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.6rem 1.1rem", fontWeight: "bold", fontSize: "0.9rem", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
      >
        ✓ Handed Off
      </button>
    </div>
  );
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

function fmtDuration(ms) {
  if (ms == null) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

function fmtShiftDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── PAST SHIFTS LIST ─────────────────────────────────────────────────────────

function PastShiftsList({ shifts, loading }) {
  const [expanded, setExpanded] = useState(null);
  if (loading) return <div style={{ color: BRAND.border, fontSize: "0.8rem", textAlign: "center", padding: "1.5rem" }}>Loading past shifts…</div>;
  if (shifts.length === 0) return null;

  return (
    <div style={{ padding: "0 1.5rem 2rem", maxWidth: "480px", margin: "0 auto" }}>
      <div style={{ color: BRAND.muted, fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", margin: "0.5rem 0 0.75rem" }}>Past Shifts</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {shifts.map(s => {
          const isOpen = expanded === s.id;
          const multiArtist = s.artistBreakdown && Object.keys(s.artistBreakdown).length > 1;
          return (
            <div key={s.id} style={{ background: BRAND.panel, borderRadius: "10px", overflow: "hidden", border: `1px solid ${BRAND.border}` }}>
              <button
                onClick={() => setExpanded(isOpen ? null : s.id)}
                style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "0.65rem 0.9rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}
              >
                <div style={{ textAlign: "left" }}>
                  <div style={{ color: BRAND.muted, fontSize: "0.82rem", fontWeight: "bold" }}>
                    {s.eventName ?? "Unnamed shift"}
                  </div>
                  <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                    {fmtShiftDate(s.closedAt)}
                    {s.durationMs ? ` · ${fmtDuration(s.durationMs)}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: BRAND.dim, fontSize: "0.8rem", fontWeight: "bold" }}>{s.totalOrders ?? 0} 🥞</div>
                    <div style={{ color: BRAND.border, fontSize: "0.65rem" }}>
                      {s.flashCount > 0 ? `${s.flashCount}⚡` : ""}{s.flashCount > 0 && s.customCount > 0 ? " " : ""}{s.customCount > 0 ? `${s.customCount}🎨` : ""}
                    </div>
                  </div>
                  <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>{isOpen ? "▲" : "▼"}</div>
                </div>
              </button>
              {isOpen && (
                <div style={{ padding: "0 0.9rem 0.75rem", borderTop: `1px solid ${BRAND.divider}`, display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  {s.bestDrawMs != null && (
                    <div style={{ color: BRAND.muted, fontSize: "0.72rem", fontFamily: "monospace" }}>
                      best draw ✏️ {fmtMs(s.bestDrawMs)}
                    </div>
                  )}
                  {multiArtist && Object.entries(s.artistBreakdown).map(([name, a]) => (
                    <div key={name} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: BRAND.dim, fontSize: "0.72rem" }}>{name}</span>
                      <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.68rem", fontFamily: "monospace" }}>
                        {a.totalOrders} orders
                        {a.bestCustomDrawMs != null ? `  C✏️${fmtMs(a.bestCustomDrawMs)}` : ""}
                        {a.bestFlashDrawMs  != null ? `  F✏️${fmtMs(a.bestFlashDrawMs)}`  : ""}
                      </span>
                    </div>
                  ))}
                  {!multiArtist && s.artistBreakdown && Object.entries(s.artistBreakdown).map(([name, a]) => (
                    <div key={name} style={{ color: BRAND.muted, fontSize: "0.68rem" }}>
                      {name}
                      {a.bestCustomDrawMs != null ? `  · Custom ✏️ ${fmtMs(a.bestCustomDrawMs)}` : ""}
                      {a.bestFlashDrawMs  != null ? `  · Flash ✏️ ${fmtMs(a.bestFlashDrawMs)}`   : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  actionButton: { padding: "1rem 1.5rem", borderRadius: "10px", border: "none", fontSize: "1rem", fontWeight: "bold", color: "white", cursor: "pointer" },
  hudButton:    { background: "transparent", border: `1px solid rgba(74,164,225,0.28)`, color: "rgba(255,255,255,0.55)", borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.8rem", cursor: "pointer", whiteSpace: "nowrap" },
  gridLabel:    { color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", display: "flex", alignItems: "center", cursor: "pointer" },
};
